import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, MessageSquare, Eye, Pin, Lock, ChevronRight, ChevronDown, Users, Clock, Flame, 
  TrendingUp, Hash, Search, Bell, Settings, Home, Bookmark, Heart, Share2, MoreHorizontal,
  Calendar, CheckCircle2, Reply, ArrowUp, ArrowDown, Filter, SortAsc, Sparkles, Star,
  FileText, Image as ImageIcon, Link2, Code, Quote, Bold, Italic, List, AtSign, Plus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

// Extended mock data
const mockCategories = [
  { id: 1, name: 'Korsordshjälp', icon: '🧩', count: 245, color: 'bg-blue-500', description: 'Få hjälp med ledtrådar' },
  { id: 2, name: 'Allmänt', icon: '💬', count: 123, color: 'bg-slate-500', description: 'Allmänna diskussioner' },
  { id: 3, name: 'Språk & Ord', icon: '📚', count: 367, color: 'bg-purple-500', description: 'Svenska språket' },
  { id: 4, name: 'Tips & Tricks', icon: '💡', count: 89, color: 'bg-amber-500', description: 'Dela strategier' },
  { id: 5, name: 'Tematiska', icon: '🎯', count: 56, color: 'bg-green-500', description: 'Teman och varianter' },
];

const mockTopics = [
  { 
    id: 1, 
    title: 'Hjälp med ledtråd: "Nordisk huvudstad (4 bokstäver)"', 
    author: 'Anna_K', 
    authorAvatar: 'A',
    replies: 12, 
    views: 156, 
    pinned: true, 
    locked: false,
    category: 'Korsordshjälp',
    categoryColor: 'bg-blue-500',
    time: '2 tim sedan',
    lastReply: { author: 'OrdNörd2000', time: '15 min sedan' },
    excerpt: 'Sitter fast på en ledtråd i dagens DN-korsord. Det ska vara en nordisk huvudstad på 4 bokstäver...',
    tags: ['ledtråd', 'geografi', 'DN'],
    solved: true,
    likes: 8
  },
  { 
    id: 2, 
    title: 'Bästa tipsen för kryptiska korsord?', 
    author: 'KorsordsFansen', 
    authorAvatar: 'K',
    replies: 34, 
    views: 423, 
    pinned: false, 
    locked: false,
    category: 'Tips & Tricks',
    categoryColor: 'bg-amber-500',
    time: '5 tim sedan',
    lastReply: { author: 'Anna_K', time: '1 tim sedan' },
    excerpt: 'Har precis börjat med kryptiska korsord och känner mig helt vilse. Någon som har bra tips?',
    tags: ['kryptiska', 'nybörjare', 'tips'],
    solved: false,
    likes: 24
  },
  { 
    id: 3, 
    title: 'Ovanliga svenska ord som börjar på X', 
    author: 'OrdNörd2000', 
    authorAvatar: 'O',
    replies: 8, 
    views: 89, 
    pinned: false,
    locked: false, 
    category: 'Språk & Ord',
    categoryColor: 'bg-purple-500',
    time: 'igår',
    lastReply: { author: 'ReseNansen', time: '3 tim sedan' },
    excerpt: 'Jag samlar på ovanliga ord och undrar om ni känner till några som börjar på X...',
    tags: ['ordlista', 'ovanliga ord', 'X'],
    solved: false,
    likes: 15
  },
  { 
    id: 4, 
    title: 'Geografiska ledtrådar - samlingstrå', 
    author: 'ReseNansen', 
    authorAvatar: 'R',
    replies: 67, 
    views: 892, 
    pinned: true, 
    locked: false,
    category: 'Korsordshjälp',
    categoryColor: 'bg-blue-500',
    time: '3 dagar sedan',
    lastReply: { author: 'GeoMaster', time: '30 min sedan' },
    excerpt: 'Här samlar vi alla ledtrådar som handlar om geografi - städer, länder, floder osv.',
    tags: ['geografi', 'samling', 'ledtrådar'],
    solved: false,
    likes: 45
  },
  { 
    id: 5, 
    title: 'Svensk-engelska falska vänner i korsord', 
    author: 'SpråkDetektiven', 
    authorAvatar: 'S',
    replies: 23, 
    views: 312, 
    pinned: false, 
    locked: false,
    category: 'Språk & Ord',
    categoryColor: 'bg-purple-500',
    time: '1 vecka sedan',
    lastReply: { author: 'Anna_K', time: '2 dagar sedan' },
    excerpt: 'Ord som ser lika ut på svenska och engelska men betyder olika saker...',
    tags: ['engelska', 'falska vänner', 'språk'],
    solved: false,
    likes: 31
  },
];

const mockReplies = [
  { id: 1, author: 'OrdNörd2000', avatar: 'O', time: '1 tim sedan', content: 'Det måste vara OSLO! Passar perfekt med 4 bokstäver.', likes: 5, isAccepted: true },
  { id: 2, author: 'KorsordsFansen', avatar: 'K', time: '45 min sedan', content: 'Kan också vara RIGA om det är Baltikum som räknas som Norden?', likes: 2, isAccepted: false },
  { id: 3, author: 'Anna_K', avatar: 'A', time: '30 min sedan', content: 'Tack allesammans! OSLO var rätt svar! 🎉', likes: 8, isAccepted: false },
];

export default function ForumDesigns() {
  const [activeDesign, setActiveDesign] = useState<number>(1);

  const designs = [
    { id: 1, name: 'Notion-stil', description: 'Wiki-liknande layout med expanderbara block och ren typografi' },
    { id: 2, name: 'Klassisk Modern', description: 'Uppdaterad klassisk design med sidebar och tydlig struktur' },
    { id: 3, name: 'Minimalist', description: 'Ultra-ren design med fokus på läsbarhet och innehåll' },
    { id: 4, name: 'Discourse', description: 'Community-fokuserad med kategorier, aktivitetsindikatorer och tidslinje' },
    { id: 5, name: 'Reddit-stil', description: 'Kortbaserad layout med röstning, kompakt och informationstät' },
    { id: 6, name: 'Stack Overflow', description: 'Q&A-fokuserat med taggar, reputation och accepterade svar' },
    { id: 7, name: 'Slack-inspirerad', description: 'Chattliknande med trådar, emoji-reaktioner och realtidskänsla' },
    { id: 8, name: 'Apple Forum', description: 'Elegant, luftigt med premium-känsla och subtila animationer' },
    { id: 9, name: 'Korsord.se-stil', description: 'Klassisk svensk anslagstavla med kompakt tabell och kategorifokus' },
    { id: 10, name: 'Flashback', description: 'Svensk traditionell forumstil med trådade diskussioner' },
    { id: 11, name: 'Twitter/X', description: 'Kortfattade inlägg med engagement-fokus och tidslinje' },
    { id: 12, name: 'Linear', description: 'Ultra-modern produktdesign med ren struktur och fokusläge' },
    { id: 13, name: 'GitHub Discussions', description: 'Utvecklarvänlig med kategorier, svar-markeringar och Markdown' },
    { id: 14, name: 'Medium/Substack', description: 'Artikelbaserat med elegant typografi och läsfokus' },
    { id: 15, name: 'Discord', description: 'Gaming-inspirerat med kanaler, trådar och rich presence' },
    { id: 16, name: 'Hacker News', description: 'Extrem minimalism med ASCII-estetik och trädstruktur' },
    { id: 17, name: 'WhatsApp/Messenger', description: 'Chattliknande med bubblor och konversationskänsla' },
    { id: 18, name: 'Google Groups', description: 'Enkel och funktionell med Google Material Design' },
    { id: 19, name: 'Tidningsstil', description: 'Klassisk tidningsestetik med kolumner och serif-typsnitt för äldre läsare' },
    { id: 20, name: 'Bibliotek', description: 'Lugn och akademisk känsla med goda kontraster och stora typsnitt' },
    { id: 21, name: 'Klassiskt Brev', description: 'Nostalgisk brevstil med papperskänsla och handskriftsinspiration' },
    { id: 22, name: 'Radio/TV', description: 'Inspirerat av SVT/SR med tydlig navigation och tillgänglighet' },
    { id: 23, name: 'Anslagstavla', description: 'Fysisk anslagstavla med lappar och häftstift - nostalgisk och tydlig' },
    { id: 24, name: 'Bokhandel', description: 'Varm träkänsla med böcker och klassisk elegans' },
    { id: 25, name: 'Kafé', description: 'Mysig caféatmosfär med jordnära färger och avslappnad känsla' },
    { id: 26, name: 'Trädgård', description: 'Naturinspirerad med gröna toner och organiska former' },
    { id: 27, name: 'Antikvariat', description: 'Gammaldags charm med sepia-toner och vintage-estetik' },
    { id: 28, name: 'Kryssning', description: 'Elegant och klassisk med nautiska inslag och premiumkänsla' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/?tab=forum">
              <Button variant="ghost" size="sm" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Tillbaka
              </Button>
            </Link>
            <Separator orientation="vertical" className="h-6" />
            <h1 className="text-xl font-semibold">Forumdesigner</h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Design selector */}
        <div className="mb-8">
          <h2 className="text-lg font-medium mb-4">Välj design:</h2>
          <div className="flex flex-wrap gap-2">
            {designs.map((design) => (
              <Button
                key={design.id}
                variant={activeDesign === design.id ? 'default' : 'outline'}
                onClick={() => setActiveDesign(design.id)}
                className="gap-2"
              >
                {design.name}
              </Button>
            ))}
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            {designs.find(d => d.id === activeDesign)?.description}
          </p>
        </div>

        {/* Design preview */}
        <div className="border rounded-xl overflow-hidden bg-card shadow-lg">
          {activeDesign === 1 && <DesignNotion />}
          {activeDesign === 2 && <DesignClassicModern />}
          {activeDesign === 3 && <DesignMinimalist />}
          {activeDesign === 4 && <DesignDiscourse />}
          {activeDesign === 5 && <DesignReddit />}
          {activeDesign === 6 && <DesignStackOverflow />}
          {activeDesign === 7 && <DesignSlack />}
          {activeDesign === 8 && <DesignApple />}
          {activeDesign === 9 && <DesignKorsordSe />}
          {activeDesign === 10 && <DesignFlashback />}
          {activeDesign === 11 && <DesignTwitter />}
          {activeDesign === 12 && <DesignLinear />}
          {activeDesign === 13 && <DesignGitHub />}
          {activeDesign === 14 && <DesignMedium />}
          {activeDesign === 15 && <DesignDiscord />}
          {activeDesign === 16 && <DesignHackerNews />}
          {activeDesign === 17 && <DesignMessenger />}
          {activeDesign === 18 && <DesignGoogleGroups />}
          {activeDesign === 19 && <DesignNewspaper />}
          {activeDesign === 20 && <DesignLibrary />}
          {activeDesign === 21 && <DesignLetter />}
          {activeDesign === 22 && <DesignBroadcast />}
          {activeDesign === 23 && <DesignBulletinBoard />}
          {activeDesign === 24 && <DesignBookstore />}
          {activeDesign === 25 && <DesignCafe />}
          {activeDesign === 26 && <DesignGarden />}
          {activeDesign === 27 && <DesignAntique />}
          {activeDesign === 28 && <DesignCruise />}
        </div>
      </main>
    </div>
  );
}

// Design 1: Notion-stil (utvecklad)
function DesignNotion() {
  const [expandedTopic, setExpandedTopic] = useState<number | null>(1);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <div className="min-h-[700px] bg-white flex">
      {/* Sidebar */}
      <aside className="w-64 border-r bg-stone-50/80 flex flex-col">
        {/* Workspace header */}
        <div className="p-3 border-b border-stone-200/60">
          <div className="flex items-center gap-2.5 px-2 py-1.5 hover:bg-stone-200/50 rounded-md cursor-pointer">
            <div className="w-6 h-6 rounded bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
              <span className="text-white text-sm">🧩</span>
            </div>
            <span className="font-semibold text-stone-800 text-sm">Korsordsforum</span>
            <ChevronDown className="h-4 w-4 text-stone-400 ml-auto" />
          </div>
        </div>

        {/* Search */}
        <div className="p-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input 
              placeholder="Sök..." 
              className="pl-8 h-8 bg-stone-100 border-0 text-sm placeholder:text-stone-400"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Quick links */}
        <nav className="px-2 space-y-0.5">
          <button className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-stone-200/50 text-sm text-stone-600 transition-colors">
            <Home className="h-4 w-4" />
            Hem
          </button>
          <button className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md bg-orange-100 text-orange-700 text-sm font-medium">
            <Flame className="h-4 w-4" />
            Populärt
          </button>
          <button className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-stone-200/50 text-sm text-stone-600 transition-colors">
            <Clock className="h-4 w-4" />
            Senaste
          </button>
          <button className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-stone-200/50 text-sm text-stone-600 transition-colors">
            <Bookmark className="h-4 w-4" />
            Sparade
          </button>
        </nav>

        <Separator className="my-4 bg-stone-200/60" />

        {/* Categories */}
        <div className="px-2 flex-1">
          <div className="flex items-center justify-between px-2 mb-2">
            <span className="text-xs font-semibold text-stone-400 uppercase tracking-wider">Kategorier</span>
            <button className="p-1 hover:bg-stone-200/50 rounded">
              <Settings className="h-3 w-3 text-stone-400" />
            </button>
          </div>
          <div className="space-y-0.5">
            {mockCategories.map((cat) => (
              <button 
                key={cat.id} 
                onClick={() => setSelectedCategory(selectedCategory === cat.id ? null : cat.id)}
                className={cn(
                  "w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm transition-colors",
                  selectedCategory === cat.id 
                    ? "bg-stone-200/70 text-stone-800" 
                    : "text-stone-600 hover:bg-stone-200/50"
                )}
              >
                <span className="text-base">{cat.icon}</span>
                <span className="flex-1 text-left">{cat.name}</span>
                <span className="text-xs text-stone-400">{cat.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* User section */}
        <div className="p-3 border-t border-stone-200/60 mt-auto">
          <div className="flex items-center gap-2.5 px-2 py-1.5 hover:bg-stone-200/50 rounded-md cursor-pointer">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs font-medium">
              A
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-stone-800 truncate">Anna_K</div>
              <div className="text-xs text-stone-400">3 nya notiser</div>
            </div>
            <Bell className="h-4 w-4 text-stone-400" />
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col">
        {/* Page header */}
        <header className="px-8 py-6 border-b border-stone-100">
          <div className="flex items-center gap-3 text-sm text-stone-400 mb-2">
            <Home className="h-4 w-4" />
            <ChevronRight className="h-3 w-3" />
            <span>Populärt</span>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-stone-800">Populära diskussioner</h1>
              <p className="text-stone-500 mt-1">De mest engagerande trådarna just nu</p>
            </div>
            <Button className="bg-stone-800 hover:bg-stone-700 text-white gap-2">
              <MessageSquare className="h-4 w-4" />
              Ny diskussion
            </Button>
          </div>
        </header>

        {/* Topic list */}
        <div className="flex-1 overflow-y-auto px-8 py-4">
          <div className="space-y-1">
            {mockTopics.map((topic) => (
              <div key={topic.id} className="group">
                <button
                  onClick={() => setExpandedTopic(expandedTopic === topic.id ? null : topic.id)}
                  className={cn(
                    "w-full flex items-start gap-3 px-4 py-3 rounded-lg text-left transition-all",
                    expandedTopic === topic.id 
                      ? "bg-stone-100" 
                      : "hover:bg-stone-50"
                  )}
                >
                  <ChevronRight className={cn(
                    "h-4 w-4 mt-1.5 text-stone-400 transition-transform flex-shrink-0",
                    expandedTopic === topic.id && "rotate-90"
                  )} />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {topic.pinned && (
                        <Pin className="h-3.5 w-3.5 text-amber-500" />
                      )}
                      {topic.solved && (
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                      )}
                      <h3 className="font-medium text-stone-800 group-hover:text-stone-600">
                        {topic.title}
                      </h3>
                    </div>
                    
                    <p className="text-sm text-stone-500 mt-1 line-clamp-1">
                      {topic.excerpt}
                    </p>
                    
                    <div className="flex items-center gap-4 mt-2">
                      <div className="flex items-center gap-1.5">
                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-[10px]">
                          {topic.authorAvatar}
                        </div>
                        <span className="text-xs text-stone-500">{topic.author}</span>
                      </div>
                      <span className={cn("text-xs px-2 py-0.5 rounded-full", topic.categoryColor, "text-white")}>
                        {topic.category}
                      </span>
                      <span className="text-xs text-stone-400">{topic.replies} svar</span>
                      <span className="text-xs text-stone-400">{topic.time}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-stone-400">
                    <Heart className="h-4 w-4" />
                    <span className="text-sm">{topic.likes}</span>
                  </div>
                </button>

                {/* Expanded content */}
                {expandedTopic === topic.id && (
                  <div className="ml-11 mr-4 mb-2 animate-in slide-in-from-top-2 duration-200">
                    <div className="p-4 bg-stone-50 rounded-lg border border-stone-200">
                      <p className="text-sm text-stone-600 leading-relaxed mb-4">
                        {topic.excerpt}
                      </p>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {topic.tags.map((tag) => (
                          <span key={tag} className="px-2 py-0.5 bg-stone-200 text-stone-600 text-xs rounded">
                            #{tag}
                          </span>
                        ))}
                      </div>

                      {/* Quick replies preview */}
                      <div className="border-t border-stone-200 pt-4 space-y-3">
                        <div className="text-xs font-medium text-stone-400 uppercase">Senaste svar</div>
                        {mockReplies.slice(0, 2).map((reply) => (
                          <div key={reply.id} className="flex gap-2.5">
                            <div className={cn(
                              "w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] flex-shrink-0",
                              reply.isAccepted ? "bg-green-500" : "bg-stone-400"
                            )}>
                              {reply.avatar}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-stone-700">{reply.author}</span>
                                {reply.isAccepted && (
                                  <Badge className="h-4 text-[10px] bg-green-100 text-green-700 border-0">
                                    ✓ Accepterat
                                  </Badge>
                                )}
                                <span className="text-xs text-stone-400">{reply.time}</span>
                              </div>
                              <p className="text-sm text-stone-600 mt-0.5">{reply.content}</p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-stone-200">
                        <Button size="sm" variant="ghost" className="h-7 text-xs gap-1.5 text-stone-600">
                          <Heart className="h-3.5 w-3.5" />
                          Gilla
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs gap-1.5 text-stone-600">
                          <Reply className="h-3.5 w-3.5" />
                          Svara
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs gap-1.5 text-stone-600">
                          <Bookmark className="h-3.5 w-3.5" />
                          Spara
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs gap-1.5 text-stone-600">
                          <Share2 className="h-3.5 w-3.5" />
                          Dela
                        </Button>
                        <Button size="sm" className="h-7 text-xs ml-auto bg-stone-800 hover:bg-stone-700">
                          Öppna tråd →
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

// Design 2: Klassisk Modern (utvecklad)
function DesignClassicModern() {
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'unanswered'>('latest');

  return (
    <div className="flex min-h-[700px] bg-slate-50">
      {/* Sidebar */}
      <aside className="w-72 bg-white border-r flex flex-col">
        {/* Logo/Brand */}
        <div className="p-4 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
              <span className="text-xl">🧩</span>
            </div>
            <div>
              <h2 className="font-bold text-slate-800">Korsordsforum</h2>
              <p className="text-xs text-slate-500">1,234 medlemmar online</p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input 
              placeholder="Sök i forumet..." 
              className="pl-9 bg-slate-50 border-slate-200"
            />
          </div>
        </div>

        {/* Categories */}
        <div className="px-4 flex-1 overflow-y-auto">
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
            Kategorier
          </h3>
          <nav className="space-y-1">
            <button
              onClick={() => setSelectedCategory(null)}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all",
                selectedCategory === null
                  ? "bg-blue-50 text-blue-700 shadow-sm"
                  : "text-slate-600 hover:bg-slate-50"
              )}
            >
              <Hash className="h-5 w-5" />
              <span className="flex-1 text-left font-medium">Alla trådar</span>
              <Badge variant="secondary" className="bg-slate-100">780</Badge>
            </button>
            
            {mockCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all",
                  selectedCategory === cat.id
                    ? "bg-blue-50 text-blue-700 shadow-sm"
                    : "text-slate-600 hover:bg-slate-50"
                )}
              >
                <span className="text-lg">{cat.icon}</span>
                <div className="flex-1 text-left">
                  <span className="font-medium block">{cat.name}</span>
                  <span className="text-xs text-slate-400">{cat.description}</span>
                </div>
                <Badge variant="secondary" className="bg-slate-100">{cat.count}</Badge>
              </button>
            ))}
          </nav>

          <Separator className="my-6" />

          {/* Quick Stats */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Forum statistik
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-slate-800">1,234</div>
                <div className="text-xs text-slate-500">Trådar</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-slate-800">8,567</div>
                <div className="text-xs text-slate-500">Inlägg</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-slate-800">456</div>
                <div className="text-xs text-slate-500">Medlemmar</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-green-600">24</div>
                <div className="text-xs text-slate-500">Online nu</div>
              </div>
            </div>
          </div>
        </div>

        {/* New Topic Button */}
        <div className="p-4 border-t">
          <Button className="w-full gap-2 bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/25">
            <MessageSquare className="h-4 w-4" />
            Ny tråd
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="bg-white border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-slate-800">
                {selectedCategory 
                  ? mockCategories.find(c => c.id === selectedCategory)?.name 
                  : 'Alla trådar'}
              </h1>
              <p className="text-sm text-slate-500 mt-0.5">
                {selectedCategory 
                  ? mockCategories.find(c => c.id === selectedCategory)?.description 
                  : 'Visa alla diskussioner i forumet'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant={sortBy === 'latest' ? 'default' : 'ghost'} 
                size="sm" 
                onClick={() => setSortBy('latest')}
                className="gap-1.5"
              >
                <Clock className="h-4 w-4" />
                Senaste
              </Button>
              <Button 
                variant={sortBy === 'popular' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setSortBy('popular')}
                className="gap-1.5"
              >
                <Flame className="h-4 w-4" />
                Populärt
              </Button>
              <Button 
                variant={sortBy === 'unanswered' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setSortBy('unanswered')}
                className="gap-1.5"
              >
                <HelpCircle className="h-4 w-4" />
                Obesvarade
              </Button>
            </div>
          </div>
        </header>

        {/* Topic list */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-3">
            {mockTopics.map((topic) => (
              <Card 
                key={topic.id} 
                className={cn(
                  "hover:shadow-md transition-all cursor-pointer group border-l-4",
                  topic.pinned ? "border-l-amber-400 bg-amber-50/30" : "border-l-transparent",
                  topic.solved && "border-l-green-400"
                )}
              >
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    {/* Author avatar */}
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold shadow-lg">
                        {topic.authorAvatar}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            {topic.pinned && (
                              <Badge className="bg-amber-100 text-amber-700 border-amber-200 gap-1">
                                <Pin className="h-3 w-3" />
                                Fastnålad
                              </Badge>
                            )}
                            {topic.solved && (
                              <Badge className="bg-green-100 text-green-700 border-green-200 gap-1">
                                <CheckCircle2 className="h-3 w-3" />
                                Löst
                              </Badge>
                            )}
                            {topic.locked && (
                              <Badge variant="secondary" className="gap-1">
                                <Lock className="h-3 w-3" />
                                Låst
                              </Badge>
                            )}
                          </div>
                          <h3 className="font-semibold text-slate-800 group-hover:text-blue-600 transition-colors">
                            {topic.title}
                          </h3>
                          <p className="text-sm text-slate-500 mt-1 line-clamp-1">
                            {topic.excerpt}
                          </p>
                        </div>

                        {/* Stats */}
                        <div className="flex items-center gap-4 text-sm text-slate-400 flex-shrink-0">
                          <div className="text-center">
                            <div className="font-semibold text-slate-700">{topic.replies}</div>
                            <div className="text-xs">svar</div>
                          </div>
                          <div className="text-center">
                            <div className="font-semibold text-slate-700">{topic.views}</div>
                            <div className="text-xs">visn.</div>
                          </div>
                        </div>
                      </div>

                      {/* Meta */}
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gap-4">
                          <span className={cn(
                            "text-xs px-2 py-1 rounded-full text-white",
                            topic.categoryColor
                          )}>
                            {topic.category}
                          </span>
                          <span className="text-xs text-slate-500">
                            av <span className="font-medium text-slate-700">{topic.author}</span> • {topic.time}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                          <div className="flex items-center gap-1">
                            <Heart className="h-3.5 w-3.5" />
                            {topic.likes}
                          </div>
                          <span>•</span>
                          <span>Senaste: {topic.lastReply.author}, {topic.lastReply.time}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Pagination */}
        <div className="bg-white border-t px-6 py-3 flex items-center justify-between">
          <span className="text-sm text-slate-500">Visar 1-5 av 245 trådar</span>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" disabled>Föregående</Button>
            <Button variant="outline" size="sm" className="bg-blue-50 border-blue-200">1</Button>
            <Button variant="outline" size="sm">2</Button>
            <Button variant="outline" size="sm">3</Button>
            <span className="px-2 text-slate-400">...</span>
            <Button variant="outline" size="sm">49</Button>
            <Button variant="outline" size="sm">Nästa</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper icon component
function HelpCircle(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="10"/>
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
      <path d="M12 17h.01"/>
    </svg>
  );
}

// Design 3: Minimalist (utvecklad)
function DesignMinimalist() {
  const [activeView, setActiveView] = useState<'all' | 'unread' | 'bookmarked'>('all');

  return (
    <div className="min-h-[700px] bg-neutral-50">
      <div className="max-w-3xl mx-auto px-8 py-12">
        {/* Minimal header */}
        <header className="mb-16">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-4xl font-light tracking-tight text-neutral-900">Forum</h1>
            <button className="flex items-center gap-2 text-neutral-400 hover:text-neutral-600 transition-colors text-sm">
              <span className="w-6 h-6 rounded-full border border-current flex items-center justify-center text-lg leading-none">+</span>
              Ny diskussion
            </button>
          </div>
          
          {/* Minimal tabs */}
          <div className="flex items-center gap-8 border-b border-neutral-200 pb-4">
            <button 
              onClick={() => setActiveView('all')}
              className={cn(
                "text-sm transition-colors relative pb-4 -mb-4",
                activeView === 'all' 
                  ? "text-neutral-900 font-medium" 
                  : "text-neutral-400 hover:text-neutral-600"
              )}
            >
              Alla
              {activeView === 'all' && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-neutral-900" />
              )}
            </button>
            <button 
              onClick={() => setActiveView('unread')}
              className={cn(
                "text-sm transition-colors relative pb-4 -mb-4",
                activeView === 'unread' 
                  ? "text-neutral-900 font-medium" 
                  : "text-neutral-400 hover:text-neutral-600"
              )}
            >
              Olästa
              <span className="ml-1.5 text-xs text-neutral-400">(3)</span>
              {activeView === 'unread' && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-neutral-900" />
              )}
            </button>
            <button 
              onClick={() => setActiveView('bookmarked')}
              className={cn(
                "text-sm transition-colors relative pb-4 -mb-4",
                activeView === 'bookmarked' 
                  ? "text-neutral-900 font-medium" 
                  : "text-neutral-400 hover:text-neutral-600"
              )}
            >
              Sparade
              {activeView === 'bookmarked' && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-neutral-900" />
              )}
            </button>
          </div>
        </header>

        {/* Topic list */}
        <div className="space-y-0">
          {mockTopics.map((topic, index) => (
            <article 
              key={topic.id} 
              className={cn(
                "group cursor-pointer py-8",
                index !== mockTopics.length - 1 && "border-b border-neutral-100"
              )}
            >
              <div className="flex items-start gap-6">
                {/* Minimal indicator */}
                <div className="mt-2 flex-shrink-0">
                  {topic.solved ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  ) : topic.pinned ? (
                    <Pin className="h-4 w-4 text-amber-500" />
                  ) : (
                    <div className="w-2 h-2 rounded-full bg-blue-400" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h2 className="text-lg text-neutral-800 group-hover:text-neutral-500 transition-colors leading-relaxed">
                    {topic.title}
                  </h2>
                  
                  <p className="text-neutral-400 mt-2 text-sm leading-relaxed line-clamp-2">
                    {topic.excerpt}
                  </p>
                  
                  <div className="flex items-center gap-6 mt-4 text-sm text-neutral-400">
                    <span className="font-medium text-neutral-600">{topic.author}</span>
                    <span className="w-1 h-1 rounded-full bg-neutral-300" />
                    <span>{topic.category}</span>
                    <span className="w-1 h-1 rounded-full bg-neutral-300" />
                    <span>{topic.time}</span>
                    <span className="w-1 h-1 rounded-full bg-neutral-300" />
                    <span>{topic.replies} svar</span>
                  </div>
                </div>

                {/* Minimal actions */}
                <div className="flex items-center gap-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button className="text-neutral-300 hover:text-neutral-500 transition-colors">
                    <Heart className="h-4 w-4" />
                  </button>
                  <button className="text-neutral-300 hover:text-neutral-500 transition-colors">
                    <Bookmark className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Minimal pagination */}
        <nav className="mt-16 flex items-center justify-center gap-4">
          <button className="text-neutral-300 hover:text-neutral-500 transition-colors">
            ← Föregående
          </button>
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((page) => (
              <button
                key={page}
                className={cn(
                  "w-8 h-8 rounded-full text-sm transition-colors",
                  page === 1 
                    ? "bg-neutral-900 text-white" 
                    : "text-neutral-400 hover:text-neutral-600"
                )}
              >
                {page}
              </button>
            ))}
          </div>
          <button className="text-neutral-600 hover:text-neutral-800 transition-colors">
            Nästa →
          </button>
        </nav>

        {/* Minimal categories footer */}
        <footer className="mt-20 pt-8 border-t border-neutral-100">
          <h3 className="text-xs uppercase tracking-[0.2em] text-neutral-300 mb-6">Kategorier</h3>
          <div className="flex flex-wrap gap-3">
            {mockCategories.map((cat) => (
              <button 
                key={cat.id} 
                className="px-4 py-2 text-sm text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-full transition-colors border border-neutral-200"
              >
                {cat.icon} {cat.name}
              </button>
            ))}
          </div>
        </footer>
      </div>
    </div>
  );
}

// Design 4: Discourse (utvecklad)
function DesignDiscourse() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  return (
    <div className="min-h-[700px] bg-slate-100">
      {/* Header */}
      <header className="bg-slate-800 text-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded bg-blue-500 flex items-center justify-center">
                  <span className="text-lg">🧩</span>
                </div>
                <span className="font-semibold text-lg">Korsordsforum</span>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Sök..."
                  className="w-64 pl-9 pr-4 py-2 bg-slate-700 border-0 rounded-lg text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 gap-1.5">
                <MessageSquare className="h-4 w-4" />
                Ny tråd
              </Button>
              <button className="p-2 hover:bg-slate-700 rounded-lg transition-colors">
                <Bell className="h-5 w-5" />
              </button>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-sm font-medium ml-1">
                A
              </div>
            </div>
          </div>
          
          {/* Category pills */}
          <div className="flex items-center gap-2 pb-3 overflow-x-auto">
            <button 
              onClick={() => setSelectedCategory(null)}
              className={cn(
                "px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors",
                selectedCategory === null 
                  ? "bg-white text-slate-800" 
                  : "bg-slate-700/50 hover:bg-slate-700"
              )}
            >
              Alla kategorier
            </button>
            {mockCategories.map((cat) => (
              <button 
                key={cat.id}
                onClick={() => setSelectedCategory(cat.name)}
                className={cn(
                  "px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1.5",
                  selectedCategory === cat.name 
                    ? "bg-white text-slate-800" 
                    : "bg-slate-700/50 hover:bg-slate-700"
                )}
              >
                <span className={cn("w-2 h-2 rounded-full", cat.color)} />
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Topic table */}
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr className="text-xs text-slate-500 uppercase tracking-wide">
                    <th className="text-left py-3 px-4 font-semibold">Ämne</th>
                    <th className="text-center py-3 px-4 w-20 font-semibold">
                      <MessageSquare className="h-4 w-4 mx-auto" />
                    </th>
                    <th className="text-center py-3 px-4 w-20 font-semibold">
                      <Eye className="h-4 w-4 mx-auto" />
                    </th>
                    <th className="text-right py-3 px-4 w-32 font-semibold">Aktivitet</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {mockTopics.map((topic) => (
                    <tr key={topic.id} className="hover:bg-slate-50 transition-colors cursor-pointer group">
                      <td className="py-4 px-4">
                        <div className="flex items-start gap-3">
                          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold flex-shrink-0 shadow-md">
                            {topic.authorAvatar}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              {topic.pinned && (
                                <Pin className="h-3.5 w-3.5 text-amber-500" />
                              )}
                              {topic.solved && (
                                <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                              )}
                              <h3 className="font-medium text-slate-800 group-hover:text-blue-600 transition-colors">
                                {topic.title}
                              </h3>
                            </div>
                            <div className="flex items-center gap-2 mt-1.5">
                              <span className={cn("w-2.5 h-2.5 rounded-full", topic.categoryColor)} />
                              <span className="text-xs text-slate-500">{topic.category}</span>
                              <span className="text-xs text-slate-400">•</span>
                              <span className="text-xs text-slate-400">{topic.author}</span>
                            </div>
                            {/* Tags */}
                            <div className="flex flex-wrap gap-1 mt-2">
                              {topic.tags.slice(0, 3).map((tag) => (
                                <span 
                                  key={tag} 
                                  className="px-1.5 py-0.5 bg-slate-100 text-slate-500 text-[10px] rounded hover:bg-slate-200 transition-colors"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="text-center py-4 px-4">
                        <span className="font-semibold text-slate-700">{topic.replies}</span>
                      </td>
                      <td className="text-center py-4 px-4 text-slate-500">
                        {topic.views}
                      </td>
                      <td className="text-right py-4 px-4">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] text-slate-600 font-medium">
                            {topic.lastReply.author[0]}
                          </div>
                          <span className="text-sm text-slate-500">{topic.lastReply.time}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Load more */}
            <div className="text-center mt-6">
              <Button variant="outline" className="gap-2">
                <ArrowDown className="h-4 w-4" />
                Ladda fler ämnen
              </Button>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="w-72 flex-shrink-0 space-y-4">
            {/* About box */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="font-semibold text-slate-800 mb-2">Om forumet</h3>
              <p className="text-sm text-slate-500 mb-4">
                Välkommen till Sveriges största forum för korsordsentusiaster! Här hittar du hjälp med ledtrådar, tips och trevliga diskussioner.
              </p>
              <div className="grid grid-cols-2 gap-3 text-center">
                <div className="bg-slate-50 rounded-lg p-2">
                  <div className="text-lg font-bold text-slate-800">1.2k</div>
                  <div className="text-xs text-slate-500">Trådar</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-2">
                  <div className="text-lg font-bold text-slate-800">8.5k</div>
                  <div className="text-xs text-slate-500">Inlägg</div>
                </div>
              </div>
            </div>

            {/* Popular tags */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="font-semibold text-slate-800 mb-3">Populära taggar</h3>
              <div className="flex flex-wrap gap-1.5">
                {['ledtråd', 'geografi', 'tips', 'DN', 'kryptiska', 'svenska', 'nybörjare', 'historia'].map((tag) => (
                  <span 
                    key={tag} 
                    className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full hover:bg-slate-200 transition-colors cursor-pointer"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Top contributors */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="font-semibold text-slate-800 mb-3">Toppbidragsgivare</h3>
              <div className="space-y-3">
                {[
                  { name: 'Anna_K', posts: 234, avatar: 'A', color: 'from-blue-400 to-blue-600' },
                  { name: 'OrdNörd2000', posts: 189, avatar: 'O', color: 'from-green-400 to-green-600' },
                  { name: 'KorsordsFansen', posts: 156, avatar: 'K', color: 'from-purple-400 to-purple-600' },
                ].map((user, i) => (
                  <div key={user.name} className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-5 text-sm font-bold text-slate-400">
                      {i + 1}
                    </div>
                    <div className={cn("w-8 h-8 rounded-full bg-gradient-to-br flex items-center justify-center text-white text-sm font-medium", user.color)}>
                      {user.avatar}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-800">{user.name}</div>
                      <div className="text-xs text-slate-400">{user.posts} inlägg</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

// Design 5: Reddit-stil
function DesignReddit() {
  const [sortBy, setSortBy] = useState<'hot' | 'new' | 'top'>('hot');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  return (
    <div className="min-h-[700px] bg-zinc-100">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center">
                <span className="text-white text-sm font-bold">K</span>
              </div>
              <div>
                <h1 className="font-bold text-zinc-900">k/korsord</h1>
                <p className="text-xs text-zinc-500">1,234 medlemmar</p>
              </div>
            </div>
            <Button size="sm" className="bg-orange-500 hover:bg-orange-600">
              Skapa inlägg
            </Button>
          </div>
        </div>
      </header>

      {/* Sort tabs */}
      <div className="bg-white border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-1 py-2">
            {[
              { key: 'hot', icon: Flame, label: 'Populärt' },
              { key: 'new', icon: Sparkles, label: 'Nytt' },
              { key: 'top', icon: TrendingUp, label: 'Topp' },
            ].map(({ key, icon: Icon, label }) => (
              <button
                key={key}
                onClick={() => setSortBy(key as typeof sortBy)}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors",
                  sortBy === key 
                    ? "bg-zinc-100 text-zinc-900" 
                    : "text-zinc-500 hover:bg-zinc-50"
                )}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Posts */}
      <div className="max-w-4xl mx-auto px-4 py-4 space-y-3">
        {mockTopics.map((topic) => (
          <div 
            key={topic.id} 
            className="bg-white rounded-lg border border-zinc-200 hover:border-zinc-300 transition-colors overflow-hidden"
          >
            <div className="flex">
              {/* Vote column */}
              <div className="flex flex-col items-center py-2 px-2 bg-zinc-50">
                <button className="p-1 text-zinc-400 hover:text-orange-500 hover:bg-orange-50 rounded">
                  <ArrowUp className="h-5 w-5" />
                </button>
                <span className="text-sm font-bold text-zinc-700 py-1">{topic.likes}</span>
                <button className="p-1 text-zinc-400 hover:text-blue-500 hover:bg-blue-50 rounded">
                  <ArrowDown className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 p-3">
                {/* Meta */}
                <div className="flex items-center gap-2 text-xs text-zinc-500 mb-1">
                  <span className={cn("px-1.5 py-0.5 rounded text-white text-[10px] font-medium", topic.categoryColor)}>
                    {topic.category}
                  </span>
                  <span>Postat av <span className="hover:underline cursor-pointer">{topic.author}</span></span>
                  <span>•</span>
                  <span>{topic.time}</span>
                  {topic.solved && (
                    <>
                      <span>•</span>
                      <span className="text-green-600 flex items-center gap-0.5">
                        <CheckCircle2 className="h-3 w-3" />
                        Löst
                      </span>
                    </>
                  )}
                </div>

                {/* Title */}
                <h2 className="font-medium text-zinc-900 hover:text-zinc-600 cursor-pointer">
                  {topic.pinned && <Pin className="h-3.5 w-3.5 text-green-600 inline mr-1.5" />}
                  {topic.title}
                </h2>

                {/* Preview */}
                <p className="text-sm text-zinc-600 mt-1 line-clamp-2">{topic.excerpt}</p>

                {/* Tags */}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {topic.tags.map((tag) => (
                    <span key={tag} className="text-xs text-blue-600 hover:text-blue-800 cursor-pointer">
                      #{tag}
                    </span>
                  ))}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-4 mt-3 text-xs text-zinc-500">
                  <button className="flex items-center gap-1 hover:bg-zinc-100 px-2 py-1 rounded transition-colors">
                    <MessageSquare className="h-4 w-4" />
                    {topic.replies} kommentarer
                  </button>
                  <button className="flex items-center gap-1 hover:bg-zinc-100 px-2 py-1 rounded transition-colors">
                    <Share2 className="h-4 w-4" />
                    Dela
                  </button>
                  <button className="flex items-center gap-1 hover:bg-zinc-100 px-2 py-1 rounded transition-colors">
                    <Bookmark className="h-4 w-4" />
                    Spara
                  </button>
                  <button className="flex items-center gap-1 hover:bg-zinc-100 px-2 py-1 rounded transition-colors">
                    <MoreHorizontal className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Design 6: Stack Overflow
function DesignStackOverflow() {
  return (
    <div className="min-h-[700px] bg-white">
      {/* Header */}
      <header className="bg-zinc-800 text-white">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <div className="w-8 h-8 rounded bg-orange-500 flex items-center justify-center">
                  <span className="text-sm font-bold">K</span>
                </div>
                <span className="font-bold text-lg">korsord</span>
                <span className="text-zinc-400 text-sm">overflow</span>
              </div>
              <nav className="flex items-center gap-1 text-sm">
                <button className="px-3 py-1.5 bg-orange-500 rounded">Frågor</button>
                <button className="px-3 py-1.5 hover:bg-zinc-700 rounded transition-colors">Taggar</button>
                <button className="px-3 py-1.5 hover:bg-zinc-700 rounded transition-colors">Användare</button>
              </nav>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                <input
                  type="text"
                  placeholder="Sök..."
                  className="w-48 pl-9 pr-4 py-1.5 bg-zinc-700 border border-zinc-600 rounded text-sm focus:outline-none focus:border-orange-500"
                />
              </div>
              <div className="w-8 h-8 rounded-sm bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-sm font-bold">
                A
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Sidebar */}
          <aside className="w-44 flex-shrink-0">
            <nav className="space-y-0.5 text-sm">
              <button className="w-full text-left px-3 py-2 bg-orange-50 text-orange-700 font-medium rounded-r-full border-l-4 border-orange-500">
                Alla frågor
              </button>
              <button className="w-full text-left px-3 py-2 text-zinc-600 hover:bg-zinc-50 rounded-r-full border-l-4 border-transparent">
                Obesvarade
              </button>
              <button className="w-full text-left px-3 py-2 text-zinc-600 hover:bg-zinc-50 rounded-r-full border-l-4 border-transparent">
                Mina frågor
              </button>
            </nav>
            <Separator className="my-4" />
            <div className="text-xs font-semibold text-zinc-400 uppercase mb-2 px-3">Populära taggar</div>
            <div className="space-y-1 px-3">
              {['ledtråd', 'geografi', 'tips', 'DN'].map((tag) => (
                <div key={tag} className="flex items-center justify-between text-sm">
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">{tag}</span>
                  <span className="text-xs text-zinc-400">× 124</span>
                </div>
              ))}
            </div>
          </aside>

          {/* Main content */}
          <main className="flex-1">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-xl font-medium text-zinc-800">Alla frågor</h1>
              <Button className="bg-blue-600 hover:bg-blue-700">Ställ fråga</Button>
            </div>

            <div className="flex items-center gap-2 mb-4 text-sm">
              <span className="text-zinc-500">245 frågor</span>
              <div className="ml-auto flex border rounded overflow-hidden">
                <button className="px-3 py-1.5 bg-zinc-100 text-zinc-700 border-r">Nyaste</button>
                <button className="px-3 py-1.5 text-zinc-500 hover:bg-zinc-50 border-r">Aktivitet</button>
                <button className="px-3 py-1.5 text-zinc-500 hover:bg-zinc-50">Obesvarade</button>
              </div>
            </div>

            {/* Questions */}
            <div className="border rounded-lg divide-y">
              {mockTopics.map((topic) => (
                <div key={topic.id} className="flex gap-4 p-4 hover:bg-zinc-50 transition-colors">
                  {/* Stats */}
                  <div className="flex flex-col items-end gap-1 text-sm w-24 flex-shrink-0">
                    <div className={cn(
                      "px-2 py-1 rounded text-center w-full",
                      topic.solved 
                        ? "bg-green-600 text-white" 
                        : topic.replies > 0 
                          ? "border border-green-600 text-green-700"
                          : "text-zinc-400"
                    )}>
                      {topic.replies} svar
                    </div>
                    <div className="text-zinc-500">{topic.likes} röster</div>
                    <div className="text-zinc-400">{topic.views} visn.</div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-blue-600 hover:text-blue-800 font-medium cursor-pointer">
                      {topic.title}
                    </h3>
                    <p className="text-sm text-zinc-600 mt-1 line-clamp-2">{topic.excerpt}</p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex flex-wrap gap-1">
                        {topic.tags.map((tag) => (
                          <span key={tag} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200 cursor-pointer">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <div className="text-xs text-zinc-500">
                        <span className="text-blue-600 hover:text-blue-800 cursor-pointer">{topic.author}</span>
                        {' '}frågade {topic.time}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

// Design 7: Slack-inspirerad
function DesignSlack() {
  const [selectedChannel, setSelectedChannel] = useState('allmänt');

  const channels = [
    { name: 'allmänt', unread: 0 },
    { name: 'ledtrådar', unread: 5 },
    { name: 'tips-tricks', unread: 2 },
    { name: 'språk-ord', unread: 0 },
    { name: 'random', unread: 12 },
  ];

  return (
    <div className="min-h-[700px] bg-white flex">
      {/* Sidebar */}
      <aside className="w-64 bg-purple-900 text-white flex flex-col">
        {/* Workspace header */}
        <div className="p-4 border-b border-purple-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                <span className="text-lg">🧩</span>
              </div>
              <div>
                <h2 className="font-bold text-sm">Korsordsforum</h2>
                <div className="flex items-center gap-1.5 text-xs text-purple-300">
                  <span className="w-2 h-2 rounded-full bg-green-400" />
                  24 online
                </div>
              </div>
            </div>
            <ChevronDown className="h-4 w-4 text-purple-400" />
          </div>
        </div>

        {/* Channels */}
        <div className="flex-1 overflow-y-auto py-2">
          <div className="px-3 mb-2">
            <div className="flex items-center justify-between text-xs text-purple-400 mb-1">
              <span className="font-semibold">Kanaler</span>
              <button className="p-0.5 hover:bg-white/10 rounded">
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          {channels.map((channel) => (
            <button
              key={channel.name}
              onClick={() => setSelectedChannel(channel.name)}
              className={cn(
                "w-full flex items-center gap-2 px-3 py-1 text-sm transition-colors",
                selectedChannel === channel.name 
                  ? "bg-blue-600" 
                  : "hover:bg-white/10 text-purple-200"
              )}
            >
              <Hash className="h-4 w-4" />
              <span className="flex-1 text-left">{channel.name}</span>
              {channel.unread > 0 && (
                <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {channel.unread}
                </span>
              )}
            </button>
          ))}
          
          <div className="px-3 mt-4 mb-2">
            <div className="text-xs text-purple-400 font-semibold">Direktmeddelanden</div>
          </div>
          {['Anna_K', 'OrdNörd2000', 'KorsordsFansen'].map((user) => (
            <button
              key={user}
              className="w-full flex items-center gap-2 px-3 py-1 text-sm text-purple-200 hover:bg-white/10 transition-colors"
            >
              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-[10px] text-white">
                {user[0]}
              </div>
              <span className="flex-1 text-left">{user}</span>
              <span className="w-2 h-2 rounded-full bg-green-400" />
            </button>
          ))}
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col">
        {/* Channel header */}
        <header className="border-b px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Hash className="h-5 w-5 text-zinc-400" />
            <h1 className="font-bold text-lg">{selectedChannel}</h1>
            <ChevronDown className="h-4 w-4 text-zinc-400" />
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm"><Users className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm"><Search className="h-4 w-4" /></Button>
          </div>
        </header>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {mockTopics.map((topic) => (
            <div key={topic.id} className="group flex gap-3 hover:bg-zinc-50 -mx-4 px-4 py-2 rounded">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold flex-shrink-0">
                {topic.authorAvatar}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-zinc-900 hover:underline cursor-pointer">{topic.author}</span>
                  <span className="text-xs text-zinc-400">{topic.time}</span>
                </div>
                <h3 className="text-zinc-900 font-medium mt-0.5">{topic.title}</h3>
                <p className="text-sm text-zinc-600 mt-1">{topic.excerpt}</p>
                
                {/* Reactions & thread */}
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex items-center gap-1 px-2 py-0.5 bg-zinc-100 hover:bg-zinc-200 rounded-full text-sm cursor-pointer">
                    <span>👍</span>
                    <span className="text-xs text-zinc-600">{topic.likes}</span>
                  </div>
                  <div className="flex items-center gap-1 px-2 py-0.5 bg-zinc-100 hover:bg-zinc-200 rounded-full text-sm cursor-pointer">
                    <span>💡</span>
                    <span className="text-xs text-zinc-600">3</span>
                  </div>
                  {topic.replies > 0 && (
                    <button className="flex items-center gap-1.5 text-blue-600 hover:underline text-sm ml-2">
                      <Reply className="h-3.5 w-3.5" />
                      {topic.replies} svar
                    </button>
                  )}
                </div>
              </div>
              
              {/* Hover actions */}
              <div className="opacity-0 group-hover:opacity-100 flex items-start gap-0.5 transition-opacity">
                <button className="p-1.5 hover:bg-zinc-200 rounded"><Heart className="h-4 w-4 text-zinc-500" /></button>
                <button className="p-1.5 hover:bg-zinc-200 rounded"><MessageSquare className="h-4 w-4 text-zinc-500" /></button>
                <button className="p-1.5 hover:bg-zinc-200 rounded"><Bookmark className="h-4 w-4 text-zinc-500" /></button>
                <button className="p-1.5 hover:bg-zinc-200 rounded"><MoreHorizontal className="h-4 w-4 text-zinc-500" /></button>
              </div>
            </div>
          ))}
        </div>

        {/* Message input */}
        <div className="border-t p-4">
          <div className="border rounded-lg bg-white overflow-hidden">
            <div className="flex items-center gap-1 px-2 py-1.5 border-b bg-zinc-50">
              <button className="p-1 hover:bg-zinc-200 rounded"><Bold className="h-4 w-4 text-zinc-500" /></button>
              <button className="p-1 hover:bg-zinc-200 rounded"><Italic className="h-4 w-4 text-zinc-500" /></button>
              <button className="p-1 hover:bg-zinc-200 rounded"><Link2 className="h-4 w-4 text-zinc-500" /></button>
              <button className="p-1 hover:bg-zinc-200 rounded"><List className="h-4 w-4 text-zinc-500" /></button>
              <button className="p-1 hover:bg-zinc-200 rounded"><Code className="h-4 w-4 text-zinc-500" /></button>
            </div>
            <textarea
              placeholder={`Meddelande till #${selectedChannel}`}
              className="w-full p-3 text-sm resize-none focus:outline-none"
              rows={2}
            />
            <div className="flex items-center justify-between px-2 py-1.5 bg-zinc-50">
              <div className="flex items-center gap-1">
                <button className="p-1 hover:bg-zinc-200 rounded"><Plus className="h-4 w-4 text-zinc-500" /></button>
                <button className="p-1 hover:bg-zinc-200 rounded"><ImageIcon className="h-4 w-4 text-zinc-500" /></button>
                <button className="p-1 hover:bg-zinc-200 rounded"><AtSign className="h-4 w-4 text-zinc-500" /></button>
              </div>
              <Button size="sm" className="bg-green-600 hover:bg-green-700">
                Skicka
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

// Design 8: Apple Forum
function DesignApple() {
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  return (
    <div className="min-h-[700px] bg-neutral-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-neutral-200/50 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                <span className="text-xl">🧩</span>
              </div>
              <div>
                <h1 className="text-xl font-semibold text-neutral-800">Korsordsforum</h1>
                <p className="text-xs text-neutral-500">Community för korsordsentusiaster</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Sök i community..."
                  className="w-64 pl-10 pr-4 py-2 bg-neutral-100 border-0 rounded-full text-sm placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                />
              </div>
              <Button className="bg-blue-600 hover:bg-blue-700 rounded-full px-6 shadow-lg shadow-blue-600/20">
                Ny diskussion
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Categories ribbon */}
      <div className="bg-white/60 backdrop-blur-sm border-b border-neutral-200/50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-2 py-3 overflow-x-auto">
            <button className="px-4 py-2 bg-neutral-800 text-white rounded-full text-sm font-medium transition-all hover:scale-105">
              Alla
            </button>
            {mockCategories.map((cat) => (
              <button
                key={cat.id}
                className="px-4 py-2 bg-white text-neutral-600 hover:bg-neutral-100 rounded-full text-sm font-medium transition-all hover:scale-105 shadow-sm flex items-center gap-2"
              >
                <span>{cat.icon}</span>
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Topics */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="space-y-4">
          {mockTopics.map((topic) => (
            <div
              key={topic.id}
              onMouseEnter={() => setHoveredId(topic.id)}
              onMouseLeave={() => setHoveredId(null)}
              className={cn(
                "bg-white rounded-2xl p-6 transition-all duration-300 cursor-pointer",
                hoveredId === topic.id 
                  ? "shadow-xl shadow-neutral-200/50 scale-[1.02]" 
                  : "shadow-sm hover:shadow-md"
              )}
            >
              <div className="flex items-start gap-4">
                {/* Avatar */}
                <div className="relative">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-semibold text-lg shadow-lg">
                    {topic.authorAvatar}
                  </div>
                  {topic.solved && (
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center shadow-sm">
                      <CheckCircle2 className="h-3 w-3 text-white" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {topic.pinned && (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex items-center gap-1">
                        <Pin className="h-3 w-3" />
                        Fastnålad
                      </span>
                    )}
                    <span className="px-2 py-0.5 bg-neutral-100 text-neutral-600 text-xs font-medium rounded-full">
                      {topic.category}
                    </span>
                  </div>
                  
                  <h2 className="text-lg font-semibold text-neutral-800 group-hover:text-blue-600 transition-colors">
                    {topic.title}
                  </h2>
                  
                  <p className="text-neutral-500 mt-1 text-sm line-clamp-2">
                    {topic.excerpt}
                  </p>

                  {/* Footer */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-neutral-100">
                    <div className="flex items-center gap-4 text-sm text-neutral-500">
                      <span className="font-medium text-neutral-700">{topic.author}</span>
                      <span>{topic.time}</span>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1.5 text-sm text-neutral-500">
                        <Heart className={cn("h-4 w-4", topic.likes > 10 && "text-red-500 fill-current")} />
                        {topic.likes}
                      </div>
                      <div className="flex items-center gap-1.5 text-sm text-neutral-500">
                        <MessageSquare className="h-4 w-4" />
                        {topic.replies}
                      </div>
                      <div className="flex items-center gap-1.5 text-sm text-neutral-500">
                        <Eye className="h-4 w-4" />
                        {topic.views}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Load more */}
        <div className="text-center mt-8">
          <Button variant="outline" className="rounded-full px-8 py-6 text-base hover:scale-105 transition-all">
            Visa fler diskussioner
          </Button>
        </div>
      </div>
    </div>
  );
}

// Design 9: Korsord.se-stil - Klassisk svensk anslagstavla
function DesignKorsordSe() {
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);

  const puzzleCategories = [
    { id: 1, name: 'DN Korsord', count: 89, color: '#336666' },
    { id: 2, name: 'SvD Korsord', count: 67, color: '#446688' },
    { id: 3, name: 'GP Korsord', count: 45, color: '#558866' },
    { id: 4, name: 'Kryptiska', count: 34, color: '#665588' },
    { id: 5, name: 'Lätta krysset', count: 123, color: '#886655' },
  ];

  return (
    <div className="min-h-[700px] bg-stone-100">
      {/* Classic header */}
      <header className="bg-gradient-to-b from-teal-800 to-teal-900 text-white">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded flex items-center justify-center">
                <span className="text-2xl">🧩</span>
              </div>
              <div>
                <h1 className="text-xl font-bold">Korsordsforum</h1>
                <p className="text-teal-200 text-xs">Sveriges största korsordscommunity</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-teal-200">Välkommen, gäst!</span>
              <Button size="sm" variant="secondary" className="bg-white/20 hover:bg-white/30 border-0 text-white">
                Logga in
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Sidebar - Avdelningar */}
          <aside className="w-56 flex-shrink-0">
            <div className="bg-white rounded shadow-sm">
              <div className="bg-teal-700 text-white px-3 py-2 rounded-t font-semibold text-sm">
                Avdelningar
              </div>
              <div className="divide-y divide-stone-100">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={cn(
                    "w-full text-left px-3 py-2 text-sm hover:bg-stone-50 transition-colors flex justify-between items-center",
                    selectedCategory === null && "bg-teal-50 text-teal-800 font-medium"
                  )}
                >
                  <span>Alla avdelningar</span>
                  <span className="text-xs text-stone-400">358</span>
                </button>
                {puzzleCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={cn(
                      "w-full text-left px-3 py-2 text-sm hover:bg-stone-50 transition-colors flex justify-between items-center",
                      selectedCategory === cat.id && "bg-teal-50 text-teal-800 font-medium"
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <span 
                        className="w-2 h-2 rounded-full" 
                        style={{ backgroundColor: cat.color }}
                      />
                      <span>{cat.name}</span>
                    </div>
                    <span className="text-xs text-stone-400">{cat.count}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Forum stats */}
            <div className="bg-white rounded shadow-sm mt-4">
              <div className="bg-stone-600 text-white px-3 py-2 rounded-t font-semibold text-sm">
                Forum statistik
              </div>
              <div className="p-3 text-xs text-stone-600 space-y-1">
                <div className="flex justify-between">
                  <span>Trådar:</span>
                  <span className="font-medium">1 234</span>
                </div>
                <div className="flex justify-between">
                  <span>Inlägg:</span>
                  <span className="font-medium">8 567</span>
                </div>
                <div className="flex justify-between">
                  <span>Medlemmar:</span>
                  <span className="font-medium">456</span>
                </div>
                <div className="flex justify-between text-green-600">
                  <span>Online nu:</span>
                  <span className="font-medium">24</span>
                </div>
              </div>
            </div>
          </aside>

          {/* Main content */}
          <main className="flex-1">
            {/* Action bar */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-stone-800">Senaste inläggen</h2>
              <Button size="sm" className="bg-teal-700 hover:bg-teal-800 gap-1.5">
                <Plus className="h-4 w-4" />
                Skapa ny tråd
              </Button>
            </div>

            {/* Thread table */}
            <div className="bg-white rounded shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-100 border-b">
                  <tr className="text-left text-stone-600">
                    <th className="px-3 py-2 font-medium">Ämne</th>
                    <th className="px-3 py-2 font-medium w-20 text-center">Svar</th>
                    <th className="px-3 py-2 font-medium w-44 text-right">Senaste inlägg</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {mockTopics.map((topic) => (
                    <tr key={topic.id} className="hover:bg-stone-50 transition-colors cursor-pointer">
                      <td className="px-3 py-3">
                        <div className="flex items-start gap-2">
                          {topic.pinned && <Pin className="h-3.5 w-3.5 text-amber-600 mt-0.5 flex-shrink-0" />}
                          <div>
                            <div className="font-medium text-teal-700 hover:underline">
                              {topic.title}
                            </div>
                            <div className="text-xs text-stone-500 mt-0.5">
                              <span 
                                className="inline-block w-2 h-2 rounded-full mr-1" 
                                style={{ backgroundColor: puzzleCategories[topic.id % puzzleCategories.length].color }}
                              />
                              {puzzleCategories[topic.id % puzzleCategories.length].name}
                              {' • '}
                              Av {topic.author}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={cn(
                          "inline-block min-w-[2rem] py-0.5 rounded text-xs font-medium",
                          topic.replies > 10 ? "bg-teal-100 text-teal-800" : "text-stone-500"
                        )}>
                          {topic.replies}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-stone-400 to-stone-500 flex items-center justify-center text-white text-[10px]">
                            {topic.lastReply.author[0]}
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-stone-700">{topic.lastReply.author}</div>
                            <div className="text-[10px] text-stone-400">{topic.lastReply.time}</div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-center gap-1 mt-4">
              <Button variant="outline" size="sm" disabled className="text-xs">« Föregående</Button>
              <Button variant="outline" size="sm" className="text-xs bg-teal-700 text-white hover:bg-teal-800 border-teal-700">1</Button>
              <Button variant="outline" size="sm" className="text-xs">2</Button>
              <Button variant="outline" size="sm" className="text-xs">3</Button>
              <span className="text-stone-400 px-2">...</span>
              <Button variant="outline" size="sm" className="text-xs">24</Button>
              <Button variant="outline" size="sm" className="text-xs">Nästa »</Button>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

// Design 10: Flashback-stil - Klassisk svensk forum
function DesignFlashback() {
  return (
    <div className="min-h-[700px] bg-neutral-200">
      {/* Header */}
      <header className="bg-neutral-800 text-white">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <h1 className="text-lg font-bold tracking-tight">KORSORDSFORUM</h1>
              <nav className="flex items-center gap-4 text-sm">
                <button className="hover:text-amber-400 transition-colors">Startsida</button>
                <button className="text-amber-400 font-medium">Forum</button>
                <button className="hover:text-amber-400 transition-colors">Sök</button>
              </nav>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-neutral-400 text-sm">Inte inloggad</span>
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-sm h-7">
                Logga in
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-neutral-300 border-y border-neutral-400/50">
        <div className="max-w-5xl mx-auto px-4 py-2 text-sm text-neutral-600">
          <span className="text-amber-700 hover:underline cursor-pointer">Forum</span>
          <span className="mx-2">›</span>
          <span>Korsordsdiskussioner</span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Forum sections */}
        <div className="space-y-4">
          {/* Section header */}
          <div className="bg-neutral-700 text-white px-4 py-2 rounded-t flex items-center justify-between">
            <h2 className="font-semibold">Korsordsdiskussioner</h2>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 h-7 text-xs gap-1">
              <Plus className="h-3 w-3" />
              Nytt ämne
            </Button>
          </div>

          {/* Thread list */}
          <div className="bg-white rounded-b shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-neutral-100 text-neutral-600 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Ämne</th>
                  <th className="text-center px-2 py-2 font-semibold w-16">Svar</th>
                  <th className="text-center px-2 py-2 font-semibold w-16">Visn.</th>
                  <th className="text-right px-4 py-2 font-semibold w-40">Senast</th>
                </tr>
              </thead>
              <tbody>
                {mockTopics.map((topic, idx) => (
                  <tr 
                    key={topic.id} 
                    className={cn(
                      "border-t border-neutral-100 hover:bg-amber-50/50 transition-colors cursor-pointer",
                      topic.pinned && "bg-amber-50"
                    )}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {topic.pinned && (
                          <span className="px-1.5 py-0.5 bg-amber-200 text-amber-800 text-[10px] font-bold rounded">
                            STICKY
                          </span>
                        )}
                        {topic.locked && (
                          <Lock className="h-3.5 w-3.5 text-neutral-400" />
                        )}
                        <div>
                          <a className="text-blue-700 hover:underline font-medium">
                            {topic.title}
                          </a>
                          <div className="text-xs text-neutral-500 mt-0.5">
                            av <span className="text-blue-600 hover:underline">{topic.author}</span>
                            {' • '}
                            Startad {topic.time}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="text-center px-2 py-3 text-neutral-600">{topic.replies}</td>
                    <td className="text-center px-2 py-3 text-neutral-500">{topic.views}</td>
                    <td className="text-right px-4 py-3">
                      <div className="text-xs">
                        <div className="text-neutral-600">{topic.lastReply.time}</div>
                        <div className="text-blue-600 hover:underline">{topic.lastReply.author}</div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Page navigation */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-neutral-500">Sida 1 av 24</span>
            <div className="flex items-center gap-1">
              {[1, 2, 3, '...', 24].map((page, i) => (
                <button
                  key={i}
                  className={cn(
                    "min-w-[2rem] h-7 rounded text-sm",
                    page === 1 
                      ? "bg-neutral-700 text-white" 
                      : "bg-white border border-neutral-300 text-neutral-600 hover:bg-neutral-100"
                  )}
                >
                  {page}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Design 11: Twitter/X-stil
function DesignTwitter() {
  return (
    <div className="min-h-[700px] bg-black text-white">
      {/* Sidebar */}
      <div className="flex max-w-5xl mx-auto">
        <aside className="w-20 flex-shrink-0 border-r border-zinc-800 py-4">
          <nav className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
              <span className="text-lg">🧩</span>
            </div>
            <button className="p-3 hover:bg-zinc-800 rounded-full transition-colors">
              <Home className="h-6 w-6" />
            </button>
            <button className="p-3 hover:bg-zinc-800 rounded-full transition-colors">
              <Search className="h-6 w-6" />
            </button>
            <button className="p-3 hover:bg-zinc-800 rounded-full transition-colors">
              <Bell className="h-6 w-6" />
            </button>
            <button className="p-3 hover:bg-zinc-800 rounded-full transition-colors">
              <Bookmark className="h-6 w-6" />
            </button>
          </nav>
        </aside>

        {/* Main feed */}
        <main className="flex-1 border-r border-zinc-800 max-w-xl">
          {/* Header */}
          <header className="border-b border-zinc-800 p-4 backdrop-blur-sm bg-black/80 sticky top-0 z-10">
            <h1 className="text-xl font-bold">Korsordsforum</h1>
          </header>

          {/* Compose */}
          <div className="border-b border-zinc-800 p-4">
            <div className="flex gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center font-bold">
                A
              </div>
              <div className="flex-1">
                <textarea 
                  placeholder="Vad händer i korsordsvärlden?"
                  className="w-full bg-transparent text-lg placeholder:text-zinc-500 resize-none focus:outline-none"
                  rows={2}
                />
                <div className="flex items-center justify-between pt-2 border-t border-zinc-800 mt-2">
                  <div className="flex items-center gap-2 text-blue-400">
                    <button className="p-2 hover:bg-blue-500/20 rounded-full"><ImageIcon className="h-5 w-5" /></button>
                    <button className="p-2 hover:bg-blue-500/20 rounded-full"><Hash className="h-5 w-5" /></button>
                  </div>
                  <Button className="bg-blue-500 hover:bg-blue-600 rounded-full px-4">
                    Posta
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-zinc-800">
            <button className="flex-1 py-4 text-center font-bold border-b-4 border-blue-500">
              För dig
            </button>
            <button className="flex-1 py-4 text-center text-zinc-500 hover:bg-zinc-900 transition-colors">
              Följer
            </button>
          </div>

          {/* Posts */}
          <div className="divide-y divide-zinc-800">
            {mockTopics.map((topic) => (
              <article key={topic.id} className="p-4 hover:bg-zinc-900/50 transition-colors cursor-pointer">
                <div className="flex gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center font-bold flex-shrink-0">
                    {topic.authorAvatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold hover:underline">{topic.author}</span>
                      <span className="text-zinc-500">@{topic.author.toLowerCase()}</span>
                      <span className="text-zinc-500">·</span>
                      <span className="text-zinc-500">{topic.time}</span>
                    </div>
                    <p className="mt-1">{topic.title}</p>
                    <p className="text-zinc-400 mt-1">{topic.excerpt}</p>
                    
                    {/* Tags as hashtags */}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {topic.tags.map((tag) => (
                        <span key={tag} className="text-blue-400 hover:underline">#{tag}</span>
                      ))}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between mt-3 text-zinc-500 max-w-sm">
                      <button className="flex items-center gap-2 hover:text-blue-400 transition-colors group">
                        <div className="p-2 group-hover:bg-blue-500/20 rounded-full">
                          <MessageSquare className="h-4 w-4" />
                        </div>
                        <span className="text-sm">{topic.replies}</span>
                      </button>
                      <button className="flex items-center gap-2 hover:text-green-400 transition-colors group">
                        <div className="p-2 group-hover:bg-green-500/20 rounded-full">
                          <Reply className="h-4 w-4" />
                        </div>
                        <span className="text-sm">{Math.floor(topic.views / 10)}</span>
                      </button>
                      <button className="flex items-center gap-2 hover:text-pink-400 transition-colors group">
                        <div className="p-2 group-hover:bg-pink-500/20 rounded-full">
                          <Heart className="h-4 w-4" />
                        </div>
                        <span className="text-sm">{topic.likes}</span>
                      </button>
                      <button className="hover:text-blue-400 transition-colors group">
                        <div className="p-2 group-hover:bg-blue-500/20 rounded-full">
                          <Share2 className="h-4 w-4" />
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </main>

        {/* Right sidebar */}
        <aside className="w-80 p-4 hidden lg:block">
          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <input
              type="text"
              placeholder="Sök"
              className="w-full pl-12 pr-4 py-3 bg-zinc-900 rounded-full border border-zinc-800 focus:border-blue-500 focus:outline-none"
            />
          </div>

          {/* Trending */}
          <div className="bg-zinc-900 rounded-xl p-4">
            <h2 className="font-bold text-xl mb-4">Trending</h2>
            <div className="space-y-4">
              {['#korsord', '#ledtråd', '#DNkryss', '#tipstricks'].map((tag, i) => (
                <div key={tag} className="hover:bg-zinc-800 -mx-4 px-4 py-2 cursor-pointer transition-colors">
                  <div className="text-xs text-zinc-500">Trending i Sverige</div>
                  <div className="font-bold">{tag}</div>
                  <div className="text-xs text-zinc-500">{1234 - i * 200} inlägg</div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

// Design 12: Linear-stil - Ultra-modern produktdesign
function DesignLinear() {
  const [selectedView, setSelectedView] = useState<'all' | 'active' | 'closed'>('all');

  return (
    <div className="min-h-[700px] bg-zinc-950 text-white">
      {/* Header */}
      <header className="border-b border-zinc-800/50">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-gradient-to-br from-violet-500 to-violet-600 rounded-lg flex items-center justify-center">
                  <span className="text-sm">🧩</span>
                </div>
                <span className="font-semibold">Korsordsforum</span>
              </div>
              <nav className="flex items-center gap-1">
                <button className="px-3 py-1.5 text-sm text-zinc-400 hover:text-white hover:bg-zinc-800/50 rounded transition-colors">
                  Diskussioner
                </button>
                <button className="px-3 py-1.5 text-sm bg-zinc-800 text-white rounded">
                  Trådar
                </button>
                <button className="px-3 py-1.5 text-sm text-zinc-400 hover:text-white hover:bg-zinc-800/50 rounded transition-colors">
                  Medlemmar
                </button>
              </nav>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                <input
                  type="text"
                  placeholder="Sök..."
                  className="w-48 pl-9 pr-4 py-1.5 bg-zinc-900 border border-zinc-800 rounded-lg text-sm focus:outline-none focus:border-violet-500 transition-colors"
                />
              </div>
              <Button size="sm" className="bg-violet-600 hover:bg-violet-700 gap-1.5 rounded-lg">
                <Plus className="h-4 w-4" />
                Ny tråd
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-6">
        {/* View tabs */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-1 p-1 bg-zinc-900 rounded-lg">
            {[
              { key: 'all', label: 'Alla', count: 245 },
              { key: 'active', label: 'Aktiva', count: 12 },
              { key: 'closed', label: 'Lösta', count: 189 },
            ].map(({ key, label, count }) => (
              <button
                key={key}
                onClick={() => setSelectedView(key as typeof selectedView)}
                className={cn(
                  "px-3 py-1.5 text-sm rounded-md transition-all flex items-center gap-2",
                  selectedView === key 
                    ? "bg-zinc-800 text-white shadow-sm" 
                    : "text-zinc-400 hover:text-white"
                )}
              >
                {label}
                <span className={cn(
                  "px-1.5 py-0.5 rounded text-xs",
                  selectedView === key ? "bg-zinc-700" : "bg-zinc-800"
                )}>
                  {count}
                </span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 text-sm text-zinc-500">
            <button className="flex items-center gap-1.5 hover:text-white transition-colors">
              <Filter className="h-4 w-4" />
              Filter
            </button>
            <button className="flex items-center gap-1.5 hover:text-white transition-colors">
              <SortAsc className="h-4 w-4" />
              Sortera
            </button>
          </div>
        </div>

        {/* Thread list */}
        <div className="space-y-1">
          {mockTopics.map((topic) => (
            <div
              key={topic.id}
              className="group flex items-center gap-4 px-4 py-3 rounded-lg hover:bg-zinc-900/50 transition-colors cursor-pointer border border-transparent hover:border-zinc-800"
            >
              {/* Status indicator */}
              <div className={cn(
                "w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0",
                topic.solved 
                  ? "border-green-500 bg-green-500" 
                  : "border-zinc-600"
              )}>
                {topic.solved && <CheckCircle2 className="h-3 w-3 text-white" />}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {topic.pinned && (
                    <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-[10px] font-medium rounded">
                      PINNED
                    </span>
                  )}
                  <h3 className="font-medium text-white truncate group-hover:text-violet-300 transition-colors">
                    {topic.title}
                  </h3>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                  <span>{topic.category}</span>
                  <span>•</span>
                  <span>{topic.author}</span>
                  <span>•</span>
                  <span>{topic.time}</span>
                </div>
              </div>

              {/* Meta */}
              <div className="flex items-center gap-4 text-zinc-500 text-sm">
                <div className="flex items-center gap-1.5">
                  <MessageSquare className="h-4 w-4" />
                  {topic.replies}
                </div>
                <div className="flex items-center gap-1.5">
                  <Eye className="h-4 w-4" />
                  {topic.views}
                </div>
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-400 to-violet-600 flex items-center justify-center text-[10px] text-white font-medium">
                  {topic.lastReply.author[0]}
                </div>
              </div>

              {/* Hover actions */}
              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                <button className="p-1.5 hover:bg-zinc-800 rounded transition-colors">
                  <Bookmark className="h-4 w-4 text-zinc-400" />
                </button>
                <button className="p-1.5 hover:bg-zinc-800 rounded transition-colors">
                  <MoreHorizontal className="h-4 w-4 text-zinc-400" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Keyboard hints */}
        <div className="flex items-center justify-center gap-6 mt-8 text-xs text-zinc-600">
          <span><kbd className="px-1.5 py-0.5 bg-zinc-800 rounded mr-1">↑↓</kbd> Navigera</span>
          <span><kbd className="px-1.5 py-0.5 bg-zinc-800 rounded mr-1">Enter</kbd> Öppna</span>
          <span><kbd className="px-1.5 py-0.5 bg-zinc-800 rounded mr-1">C</kbd> Ny tråd</span>
          <span><kbd className="px-1.5 py-0.5 bg-zinc-800 rounded mr-1">/</kbd> Sök</span>
        </div>
      </div>
    </div>
  );
}

// Design 13: GitHub Discussions
function DesignGitHub() {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  return (
    <div className="min-h-[700px] bg-[#0d1117] text-[#c9d1d9]">
      {/* Header */}
      <header className="bg-[#161b22] border-b border-[#30363d] px-6 py-3">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <svg className="h-8 w-8 text-white" viewBox="0 0 16 16" fill="currentColor">
                <path fillRule="evenodd" d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/>
              </svg>
              <span className="font-semibold text-white">korsordslexikon</span>
              <span className="text-[#8b949e]">/</span>
              <span className="text-[#58a6ff]">forum</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8b949e]" />
              <input
                type="text"
                placeholder="Sök diskussioner..."
                className="w-72 pl-9 pr-4 py-1.5 bg-[#0d1117] border border-[#30363d] rounded-md text-sm focus:outline-none focus:border-[#58a6ff] focus:ring-1 focus:ring-[#58a6ff]"
              />
            </div>
            <Button size="sm" className="bg-[#238636] hover:bg-[#2ea043] text-white border-0">
              Ny diskussion
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="flex gap-6">
          {/* Sidebar */}
          <aside className="w-64 flex-shrink-0">
            <div className="space-y-1">
              {[
                { key: 'all', label: 'Alla diskussioner', icon: '💬', count: 245 },
                { key: 'announcements', label: 'Nyheter', icon: '📢', count: 12 },
                { key: 'ideas', label: 'Idéer', icon: '💡', count: 34 },
                { key: 'qa', label: 'Frågor & Svar', icon: '❓', count: 156 },
                { key: 'show', label: 'Visa & Berätta', icon: '🎉', count: 43 },
              ].map(({ key, label, icon, count }) => (
                <button
                  key={key}
                  onClick={() => setSelectedCategory(key)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                    selectedCategory === key 
                      ? "bg-[#21262d] text-white" 
                      : "text-[#8b949e] hover:bg-[#21262d] hover:text-[#c9d1d9]"
                  )}
                >
                  <span>{icon}</span>
                  <span className="flex-1 text-left">{label}</span>
                  <span className="text-xs bg-[#30363d] px-2 py-0.5 rounded-full">{count}</span>
                </button>
              ))}
            </div>

            <Separator className="my-4 bg-[#30363d]" />

            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-[#8b949e] uppercase px-3">Populära taggar</h3>
              <div className="flex flex-wrap gap-2 px-3">
                {['ledtråd', 'tips', 'geografi', 'språk', 'nybörjare'].map((tag) => (
                  <span key={tag} className="px-2 py-1 bg-[#388bfd26] text-[#58a6ff] text-xs rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </aside>

          {/* Main content */}
          <main className="flex-1">
            <div className="border border-[#30363d] rounded-md overflow-hidden">
              {/* List header */}
              <div className="bg-[#161b22] px-4 py-3 flex items-center gap-4 text-sm">
                <button className="font-semibold text-white">Öppna</button>
                <button className="text-[#8b949e] hover:text-[#c9d1d9]">Stängda</button>
                <div className="flex-1" />
                <button className="flex items-center gap-1 text-[#8b949e] hover:text-[#c9d1d9]">
                  <SortAsc className="h-4 w-4" />
                  Sortera
                </button>
              </div>

              {/* Discussion list */}
              <div className="divide-y divide-[#21262d]">
                {mockTopics.map((topic) => (
                  <div key={topic.id} className="px-4 py-3 hover:bg-[#161b22] transition-colors">
                    <div className="flex gap-3">
                      {/* Category icon */}
                      <div className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                        topic.solved ? "bg-[#238636]" : "bg-[#388bfd26]"
                      )}>
                        {topic.solved ? (
                          <CheckCircle2 className="h-4 w-4 text-white" />
                        ) : (
                          <MessageSquare className="h-4 w-4 text-[#58a6ff]" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {topic.pinned && (
                            <Pin className="h-4 w-4 text-[#8b949e]" />
                          )}
                          <a href="#" className="font-semibold text-white hover:text-[#58a6ff]">
                            {topic.title}
                          </a>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-[#8b949e]">
                          <span>#{topic.id}</span>
                          <span>av {topic.author}</span>
                          <span>•</span>
                          <span>{topic.time}</span>
                          {topic.solved && (
                            <>
                              <span>•</span>
                              <span className="text-[#3fb950]">✓ Löst</span>
                            </>
                          )}
                        </div>
                        <div className="flex gap-1.5 mt-2">
                          {topic.tags.map((tag) => (
                            <span key={tag} className="px-2 py-0.5 bg-[#388bfd26] text-[#58a6ff] text-xs rounded-full">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-1 text-xs text-[#8b949e]">
                        <div className="flex items-center gap-1">
                          <MessageSquare className="h-3.5 w-3.5" />
                          {topic.replies}
                        </div>
                        <div className="flex -space-x-1">
                          <div className="w-5 h-5 rounded-full bg-gradient-to-br from-green-400 to-green-600 border-2 border-[#0d1117]" />
                          <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 border-2 border-[#0d1117]" />
                          <div className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 border-2 border-[#0d1117]" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

// Design 14: Medium/Substack
function DesignMedium() {
  return (
    <div className="min-h-[700px] bg-white">
      {/* Header */}
      <header className="border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <h1 className="text-2xl font-serif font-bold text-gray-900">Korsordsforum</h1>
            <nav className="hidden md:flex items-center gap-6 text-sm">
              <a href="#" className="text-gray-600 hover:text-gray-900">Senaste</a>
              <a href="#" className="text-gray-600 hover:text-gray-900">Populärt</a>
              <a href="#" className="text-gray-600 hover:text-gray-900">Ämnen</a>
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <button className="p-2 hover:bg-gray-100 rounded-full">
              <Search className="h-5 w-5 text-gray-600" />
            </button>
            <button className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-full">
              Skriv
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        {/* Featured post */}
        <article className="mb-12 pb-12 border-b border-gray-200">
          <div className="flex gap-8">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-sm font-medium">
                  R
                </div>
                <span className="text-sm text-gray-700">ReseNansen</span>
                <span className="text-gray-400">·</span>
                <span className="text-sm text-gray-500">3 dagar sedan</span>
              </div>
              <h2 className="text-2xl font-serif font-bold text-gray-900 mb-3 hover:text-gray-600 cursor-pointer">
                Geografiska ledtrådar - en komplett guide
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Här samlar vi alla ledtrådar som handlar om geografi - städer, länder, floder osv. En oumbärlig resurs för alla korsordsälskare som vill bli bättre på geografiska frågor...
              </p>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span>8 min läsning</span>
                <span className="flex items-center gap-1">
                  <Star className="h-4 w-4" />
                  Utvalt
                </span>
                <div className="flex items-center gap-1">
                  <MessageSquare className="h-4 w-4" />
                  67 kommentarer
                </div>
              </div>
            </div>
            <div className="w-48 h-32 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-4xl">🗺️</span>
            </div>
          </div>
        </article>

        {/* Post list */}
        <div className="space-y-8">
          {mockTopics.slice(0, 4).map((topic) => (
            <article key={topic.id} className="flex gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs">
                    {topic.authorAvatar}
                  </div>
                  <span className="text-sm text-gray-700">{topic.author}</span>
                  <span className="text-gray-400">·</span>
                  <span className="text-sm text-gray-500">{topic.time}</span>
                </div>
                <h3 className="text-lg font-serif font-bold text-gray-900 mb-2 hover:text-gray-600 cursor-pointer">
                  {topic.title}
                </h3>
                <p className="text-gray-500 text-sm line-clamp-2 mb-3">
                  {topic.excerpt}
                </p>
                <div className="flex items-center gap-3 text-sm text-gray-500">
                  <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">{topic.category}</span>
                  <span>{Math.ceil(topic.replies / 10)} min läsning</span>
                  {topic.solved && (
                    <span className="flex items-center gap-1 text-green-600">
                      <CheckCircle2 className="h-4 w-4" />
                      Löst
                    </span>
                  )}
                </div>
              </div>
              <div className="w-24 h-24 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">{mockCategories.find(c => c.name === topic.category)?.icon || '💬'}</span>
              </div>
            </article>
          ))}
        </div>
      </main>
    </div>
  );
}

// Design 15: Discord
function DesignDiscord() {
  const [selectedChannel, setSelectedChannel] = useState('korsordshjälp');

  return (
    <div className="min-h-[700px] bg-[#313338] text-[#dbdee1] flex">
      {/* Server sidebar */}
      <aside className="w-[72px] bg-[#1e1f22] flex flex-col items-center py-3 gap-2">
        <div className="w-12 h-12 rounded-2xl bg-[#5865f2] flex items-center justify-center text-white font-bold hover:rounded-xl transition-all cursor-pointer">
          🧩
        </div>
        <div className="w-8 h-0.5 bg-[#35363c] rounded-full my-1" />
        {['💬', '📚', '🎮', '🎵'].map((icon, i) => (
          <div key={i} className="w-12 h-12 rounded-full bg-[#313338] flex items-center justify-center hover:rounded-xl hover:bg-[#5865f2] transition-all cursor-pointer">
            {icon}
          </div>
        ))}
        <div className="mt-auto">
          <div className="w-12 h-12 rounded-full bg-[#23a559] flex items-center justify-center hover:rounded-xl transition-all cursor-pointer">
            <Plus className="h-5 w-5 text-white" />
          </div>
        </div>
      </aside>

      {/* Channel sidebar */}
      <aside className="w-60 bg-[#2b2d31] flex flex-col">
        <div className="h-12 px-4 flex items-center border-b border-[#1f2023] shadow-sm">
          <h2 className="font-semibold text-white">Korsordsforum</h2>
          <ChevronDown className="h-4 w-4 ml-auto text-[#b5bac1]" />
        </div>

        <div className="flex-1 overflow-y-auto py-4 px-2">
          <div className="mb-4">
            <button className="w-full flex items-center gap-1.5 px-2 py-1 text-xs font-semibold text-[#949ba4] uppercase hover:text-[#dbdee1]">
              <ChevronDown className="h-3 w-3" />
              Kategorier
            </button>
            {mockCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedChannel(cat.name.toLowerCase())}
                className={cn(
                  "w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm transition-colors",
                  selectedChannel === cat.name.toLowerCase()
                    ? "bg-[#404249] text-white"
                    : "text-[#949ba4] hover:text-[#dbdee1] hover:bg-[#35373c]"
                )}
              >
                <Hash className="h-4 w-4 text-[#80848e]" />
                {cat.name.toLowerCase().replace(' ', '-')}
                {cat.count > 100 && (
                  <span className="ml-auto w-4 h-4 bg-[#f23f43] rounded-full text-[10px] text-white flex items-center justify-center">
                    !
                  </span>
                )}
              </button>
            ))}
          </div>

          <div>
            <button className="w-full flex items-center gap-1.5 px-2 py-1 text-xs font-semibold text-[#949ba4] uppercase hover:text-[#dbdee1]">
              <ChevronDown className="h-3 w-3" />
              Röstkanaler
            </button>
            <button className="w-full flex items-center gap-2 px-2 py-1.5 text-[#949ba4] hover:text-[#dbdee1] hover:bg-[#35373c] rounded text-sm">
              <span className="text-[#80848e]">🔊</span>
              Lounge
              <span className="ml-auto text-xs text-[#949ba4]">3</span>
            </button>
          </div>
        </div>

        {/* User panel */}
        <div className="h-[52px] bg-[#232428] flex items-center px-2 gap-2">
          <div className="relative">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-sm">
              A
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-[#23a559] rounded-full border-2 border-[#232428]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-white truncate">Anna_K</div>
            <div className="text-xs text-[#949ba4]">Online</div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col">
        {/* Channel header */}
        <header className="h-12 px-4 flex items-center gap-2 border-b border-[#1f2023] shadow-sm">
          <Hash className="h-5 w-5 text-[#80848e]" />
          <span className="font-semibold text-white">{selectedChannel}</span>
          <div className="w-px h-6 bg-[#3f4147] mx-2" />
          <span className="text-sm text-[#949ba4]">Få hjälp med ledtrådar</span>
          <div className="flex-1" />
          <button className="p-1.5 hover:bg-[#3f4147] rounded">
            <Search className="h-5 w-5 text-[#b5bac1]" />
          </button>
        </header>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {mockTopics.map((topic) => (
            <div key={topic.id} className="flex gap-4 hover:bg-[#2e3035] rounded px-2 py-1 -mx-2">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-medium flex-shrink-0">
                {topic.authorAvatar}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-white hover:underline cursor-pointer">{topic.author}</span>
                  <span className="text-xs text-[#949ba4]">{topic.time}</span>
                </div>
                <h3 className="font-semibold text-white mt-1">{topic.title}</h3>
                <p className="text-[#dbdee1] text-sm mt-1">{topic.excerpt}</p>
                
                {/* Thread indicator */}
                <button className="flex items-center gap-2 mt-2 px-3 py-1.5 bg-[#3f4147] hover:bg-[#4f545c] rounded text-sm transition-colors">
                  <MessageSquare className="h-4 w-4 text-[#949ba4]" />
                  <span className="text-[#00a8fc]">{topic.replies} svar</span>
                  <span className="text-[#949ba4]">•</span>
                  <span className="text-[#949ba4]">Senaste {topic.lastReply.time}</span>
                </button>

                {/* Reactions */}
                <div className="flex gap-1 mt-2">
                  <button className="flex items-center gap-1 px-2 py-0.5 bg-[#3f4147] hover:bg-[#4f545c] rounded text-xs">
                    👍 {topic.likes}
                  </button>
                  {topic.solved && (
                    <button className="flex items-center gap-1 px-2 py-0.5 bg-[#248046] rounded text-xs text-white">
                      ✅ Löst
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Message input */}
        <div className="p-4">
          <div className="bg-[#383a40] rounded-lg flex items-center px-4 py-3">
            <button className="p-1 hover:text-white text-[#b5bac1]">
              <Plus className="h-5 w-5" />
            </button>
            <input
              type="text"
              placeholder={`Skicka ett meddelande i #${selectedChannel}`}
              className="flex-1 bg-transparent text-[#dbdee1] placeholder-[#6d6f78] outline-none px-3"
            />
            <div className="flex items-center gap-2 text-[#b5bac1]">
              <button className="p-1 hover:text-white">😀</button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

// Design 16: Hacker News
function DesignHackerNews() {
  return (
    <div className="min-h-[700px] bg-[#f6f6ef]">
      {/* Header */}
      <header className="bg-[#ff6600] px-2 py-0.5">
        <div className="flex items-center gap-2 max-w-5xl mx-auto text-sm">
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 border-2 border-white flex items-center justify-center">
              <span className="text-white text-xs font-bold">K</span>
            </div>
            <a href="#" className="font-bold text-black hover:underline">Korsordsforum</a>
          </div>
          <span className="text-black">|</span>
          <nav className="flex items-center gap-2 text-black">
            <a href="#" className="hover:underline">nya</a>
            <span>|</span>
            <a href="#" className="hover:underline">trådar</a>
            <span>|</span>
            <a href="#" className="hover:underline">förbi</a>
            <span>|</span>
            <a href="#" className="hover:underline">fråga</a>
            <span>|</span>
            <a href="#" className="hover:underline">visa</a>
            <span>|</span>
            <a href="#" className="hover:underline">jobb</a>
            <span>|</span>
            <a href="#" className="hover:underline">skicka in</a>
          </nav>
          <div className="flex-1" />
          <a href="#" className="text-black hover:underline">logga in</a>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-2 py-2">
        <table className="w-full text-sm" cellPadding={0} cellSpacing={0}>
          <tbody>
            {mockTopics.map((topic, i) => (
              <>
                <tr key={topic.id}>
                  <td className="text-right text-[#828282] pr-1 align-top pt-1 w-8">
                    {i + 1}.
                  </td>
                  <td className="align-top pt-1 w-4">
                    <button className="text-[#828282] hover:text-[#ff6600]">▲</button>
                  </td>
                  <td className="pt-1">
                    <a href="#" className="text-black visited:text-[#828282] hover:underline">
                      {topic.title}
                    </a>
                    {' '}
                    <span className="text-xs text-[#828282]">
                      ({topic.category.toLowerCase()})
                    </span>
                  </td>
                </tr>
                <tr>
                  <td colSpan={2} />
                  <td className="text-xs text-[#828282] pb-2">
                    {topic.likes} poäng av{' '}
                    <a href="#" className="hover:underline">{topic.author}</a>
                    {' '}{topic.time}{' | '}
                    <a href="#" className="hover:underline">göm</a>
                    {' | '}
                    <a href="#" className="hover:underline">{topic.replies} kommentarer</a>
                    {topic.solved && (
                      <>
                        {' | '}
                        <span className="text-green-700">[löst]</span>
                      </>
                    )}
                  </td>
                </tr>
              </>
            ))}
            <tr className="h-4" />
            <tr>
              <td colSpan={3}>
                <a href="#" className="text-black hover:underline text-sm">Mer</a>
              </td>
            </tr>
          </tbody>
        </table>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#ff6600] mt-8 pt-4 pb-8">
        <div className="max-w-5xl mx-auto px-2 text-center text-xs text-[#828282]">
          <div className="mb-2">
            <a href="#" className="hover:underline">Riktlinjer</a>
            {' | '}
            <a href="#" className="hover:underline">FAQ</a>
            {' | '}
            <a href="#" className="hover:underline">Listor</a>
            {' | '}
            <a href="#" className="hover:underline">API</a>
            {' | '}
            <a href="#" className="hover:underline">Säkerhet</a>
            {' | '}
            <a href="#" className="hover:underline">Juridik</a>
            {' | '}
            <a href="#" className="hover:underline">Ansök</a>
            {' | '}
            <a href="#" className="hover:underline">Kontakt</a>
          </div>
          <div>
            <label>
              Sök: <input type="text" size={17} className="border border-black px-1" />
            </label>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Design 17: WhatsApp/Messenger
function DesignMessenger() {
  const [selectedTopic, setSelectedTopic] = useState(mockTopics[0]);

  return (
    <div className="min-h-[700px] bg-[#111b21] flex">
      {/* Conversation list */}
      <aside className="w-96 bg-[#111b21] border-r border-[#222d34] flex flex-col">
        {/* Header */}
        <div className="h-14 px-4 flex items-center justify-between bg-[#202c33]">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white">
            A
          </div>
          <div className="flex items-center gap-4 text-[#aebac1]">
            <button className="p-2 hover:bg-[#374248] rounded-full">
              <Users className="h-5 w-5" />
            </button>
            <button className="p-2 hover:bg-[#374248] rounded-full">
              <MessageSquare className="h-5 w-5" />
            </button>
            <button className="p-2 hover:bg-[#374248] rounded-full">
              <MoreHorizontal className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-3 py-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8696a0]" />
            <input
              type="text"
              placeholder="Sök eller starta nytt ämne"
              className="w-full pl-10 pr-4 py-2 bg-[#202c33] rounded-lg text-sm text-[#d1d7db] placeholder-[#8696a0] outline-none"
            />
          </div>
        </div>

        {/* Chat list */}
        <div className="flex-1 overflow-y-auto">
          {mockTopics.map((topic) => (
            <button
              key={topic.id}
              onClick={() => setSelectedTopic(topic)}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-3 hover:bg-[#202c33] transition-colors",
                selectedTopic.id === topic.id && "bg-[#2a3942]"
              )}
            >
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-medium flex-shrink-0">
                {topic.category[0]}
              </div>
              <div className="flex-1 min-w-0 text-left">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-[#e9edef] truncate">{topic.category}</span>
                  <span className="text-xs text-[#8696a0]">{topic.time}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-[#8696a0] truncate">{topic.author}: {topic.title}</span>
                </div>
              </div>
              {topic.replies > 10 && (
                <div className="w-5 h-5 bg-[#00a884] rounded-full flex items-center justify-center">
                  <span className="text-xs text-white font-medium">{topic.replies}</span>
                </div>
              )}
            </button>
          ))}
        </div>
      </aside>

      {/* Chat view */}
      <main className="flex-1 flex flex-col bg-[#0b141a]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23182229' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }}>
        {/* Header */}
        <div className="h-14 px-4 flex items-center gap-3 bg-[#202c33]">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white">
            {selectedTopic.category[0]}
          </div>
          <div className="flex-1">
            <div className="font-medium text-[#e9edef]">{selectedTopic.category}</div>
            <div className="text-xs text-[#8696a0]">{selectedTopic.replies + 3} deltagare</div>
          </div>
          <button className="p-2 text-[#aebac1] hover:bg-[#374248] rounded-full">
            <Search className="h-5 w-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* System message */}
          <div className="flex justify-center">
            <span className="px-3 py-1 bg-[#182229] rounded-lg text-xs text-[#8696a0]">
              Tråden skapades {selectedTopic.time}
            </span>
          </div>

          {/* Original post */}
          <div className="flex gap-2 max-w-md">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs flex-shrink-0 mt-1">
              {selectedTopic.authorAvatar}
            </div>
            <div className="bg-[#202c33] rounded-lg rounded-tl-none p-3 shadow-sm">
              <div className="text-xs text-[#00a884] font-medium mb-1">{selectedTopic.author}</div>
              <div className="font-medium text-[#e9edef] mb-1">{selectedTopic.title}</div>
              <div className="text-sm text-[#d1d7db]">{selectedTopic.excerpt}</div>
              <div className="text-[10px] text-[#8696a0] text-right mt-1">{selectedTopic.time}</div>
            </div>
          </div>

          {/* Replies */}
          {mockReplies.map((reply, i) => (
            <div key={reply.id} className={cn("flex gap-2", i % 2 === 1 ? "justify-end" : "max-w-md")}>
              {i % 2 === 0 && (
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white text-xs flex-shrink-0 mt-1">
                  {reply.avatar}
                </div>
              )}
              <div className={cn(
                "rounded-lg p-3 shadow-sm max-w-md",
                i % 2 === 1 
                  ? "bg-[#005c4b] rounded-tr-none" 
                  : "bg-[#202c33] rounded-tl-none"
              )}>
                {i % 2 === 0 && (
                  <div className="text-xs text-[#f7931e] font-medium mb-1">{reply.author}</div>
                )}
                <div className="text-sm text-[#d1d7db]">{reply.content}</div>
                <div className="flex items-center justify-end gap-1 mt-1">
                  <span className="text-[10px] text-[#8696a0]">{reply.time}</span>
                  {i % 2 === 1 && <CheckCircle2 className="h-3 w-3 text-[#53bdeb]" />}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Input */}
        <div className="px-4 py-3 bg-[#202c33]">
          <div className="flex items-center gap-2">
            <button className="p-2 text-[#8696a0] hover:text-[#d1d7db]">
              <span className="text-xl">😊</span>
            </button>
            <button className="p-2 text-[#8696a0] hover:text-[#d1d7db]">
              <Plus className="h-6 w-6" />
            </button>
            <input
              type="text"
              placeholder="Skriv ett meddelande"
              className="flex-1 py-2.5 px-4 bg-[#2a3942] rounded-lg text-[#d1d7db] placeholder-[#8696a0] outline-none text-sm"
            />
            <button className="p-2 text-[#8696a0] hover:text-[#d1d7db]">
              🎤
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

// Design 18: Google Groups
function DesignGoogleGroups() {
  return (
    <div className="min-h-[700px] bg-white">
      {/* Header */}
      <header className="h-16 px-4 flex items-center gap-4 border-b border-gray-200">
        <button className="p-2 hover:bg-gray-100 rounded-full">
          <svg className="h-6 w-6 text-gray-600" viewBox="0 0 24 24">
            <path fill="currentColor" d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
          </svg>
        </button>
        <div className="flex items-center gap-2">
          <svg className="h-10 w-10" viewBox="0 0 48 48">
            <path fill="#4285f4" d="M24 4c-5.52 0-10 4.48-10 10s4.48 10 10 10 10-4.48 10-10S29.52 4 24 4z"/>
            <path fill="#34a853" d="M24 24c-5.52 0-10 4.48-10 10v10h20V34c0-5.52-4.48-10-10-10z"/>
            <path fill="#fbbc05" d="M14 24c-5.52 0-10 4.48-10 10v10h10V24z"/>
            <path fill="#ea4335" d="M34 24v20h10V34c0-5.52-4.48-10-10-10z"/>
          </svg>
          <span className="text-xl text-gray-700">Grupper</span>
        </div>
        <div className="flex-1 max-w-2xl mx-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Sök i alla konversationer"
              className="w-full pl-10 pr-4 py-2.5 bg-gray-100 hover:bg-gray-200 focus:bg-white focus:shadow-md rounded-lg outline-none transition-all text-sm"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button className="p-2 hover:bg-gray-100 rounded-full">
            <Settings className="h-5 w-5 text-gray-600" />
          </button>
          <button className="p-2 hover:bg-gray-100 rounded-full">
            <Bell className="h-5 w-5 text-gray-600" />
          </button>
          <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white text-sm ml-2">
            A
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 border-r border-gray-200 p-2">
          <Button className="w-full mb-4 bg-white hover:bg-gray-50 text-gray-700 border shadow-sm gap-2 justify-start px-6">
            <Plus className="h-5 w-5 text-gray-600" />
            Ny konversation
          </Button>

          <nav className="space-y-0.5">
            {[
              { icon: '📥', label: 'Mina konversationer', active: true },
              { icon: '⭐', label: 'Stjärnmärkta', active: false },
              { icon: '📋', label: 'Alla ämnen', active: false },
            ].map((item) => (
              <button
                key={item.label}
                className={cn(
                  "w-full flex items-center gap-3 px-4 py-2 rounded-full text-sm transition-colors",
                  item.active 
                    ? "bg-blue-50 text-blue-700" 
                    : "text-gray-700 hover:bg-gray-100"
                )}
              >
                <span>{item.icon}</span>
                {item.label}
              </button>
            ))}
          </nav>

          <Separator className="my-4" />

          <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase">Kategorier</div>
          <nav className="space-y-0.5">
            {mockCategories.map((cat) => (
              <button
                key={cat.id}
                className="w-full flex items-center gap-3 px-4 py-2 rounded-full text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <span>{cat.icon}</span>
                <span className="flex-1 text-left">{cat.name}</span>
                <span className="text-xs text-gray-500">{cat.count}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Main content */}
        <main className="flex-1 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Alla konversationer</h2>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <button className="px-3 py-1.5 hover:bg-gray-100 rounded flex items-center gap-1">
                <Filter className="h-4 w-4" />
                Filter
              </button>
              <button className="px-3 py-1.5 hover:bg-gray-100 rounded flex items-center gap-1">
                <SortAsc className="h-4 w-4" />
                Sortera
              </button>
            </div>
          </div>

          {/* Thread list */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            {mockTopics.map((topic, i) => (
              <div
                key={topic.id}
                className={cn(
                  "flex items-center gap-4 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors",
                  i > 0 && "border-t border-gray-100"
                )}
              >
                <input type="checkbox" className="w-4 h-4 rounded border-gray-300" />
                <button className="text-gray-400 hover:text-yellow-500">
                  <Star className="h-5 w-5" />
                </button>
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-sm flex-shrink-0">
                  {topic.authorAvatar}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 truncate">{topic.title}</span>
                    {topic.pinned && (
                      <Pin className="h-4 w-4 text-gray-400" />
                    )}
                    {topic.solved && (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    )}
                  </div>
                  <div className="text-sm text-gray-500 truncate">
                    {topic.author} - {topic.excerpt}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-sm text-gray-500">{topic.time}</div>
                  <div className="text-xs text-gray-400">{topic.replies} svar</div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
            <span>1–5 av 245</span>
            <div className="flex items-center gap-1">
              <button className="p-1.5 hover:bg-gray-100 rounded disabled:opacity-50" disabled>
                <ChevronRight className="h-5 w-5 rotate-180" />
              </button>
              <button className="p-1.5 hover:bg-gray-100 rounded">
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

// Design 19: Tidningsstil - Klassisk tidningsestetik
function DesignNewspaper() {
  return (
    <div className="min-h-[700px] bg-[#faf8f5]">
      {/* Masthead */}
      <header className="bg-[#1a1a1a] text-white py-2 px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between text-sm">
          <span>Onsdag 15 januari 2025</span>
          <span>Medlemsexklusivt</span>
        </div>
      </header>

      <div className="border-b-4 border-[#1a1a1a] bg-[#faf8f5]">
        <div className="max-w-5xl mx-auto py-6 text-center">
          <h1 className="text-5xl font-serif font-bold text-[#1a1a1a] tracking-tight">
            KORSORDSFORUM
          </h1>
          <p className="text-sm text-gray-600 mt-2 font-serif italic">
            Sveriges ledande community för korsordsentusiaster sedan 2024
          </p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="border-b border-gray-300 bg-[#faf8f5]">
        <div className="max-w-5xl mx-auto flex justify-center gap-8 py-3 text-sm font-medium">
          {['Senaste', 'Ledtrådar', 'Språk', 'Tips', 'Arkiv'].map((item) => (
            <button key={item} className="text-gray-700 hover:text-[#1a1a1a] transition-colors uppercase tracking-wide">
              {item}
            </button>
          ))}
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="grid grid-cols-3 gap-8">
          {/* Main column */}
          <div className="col-span-2 space-y-6">
            {/* Featured article */}
            <article className="border-b-2 border-gray-200 pb-6">
              <span className="text-xs font-bold text-red-700 uppercase tracking-wider">Utvalt</span>
              <h2 className="text-3xl font-serif font-bold text-[#1a1a1a] mt-2 leading-tight hover:text-gray-600 cursor-pointer">
                Geografiska ledtrådar — en komplett samling för korsordsälskaren
              </h2>
              <p className="text-lg text-gray-600 mt-3 font-serif leading-relaxed">
                Här samlar vi alla ledtrådar som handlar om geografi. Städer, länder, floder och berg — allt du behöver för att lösa även de svåraste korsordet.
              </p>
              <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
                <span className="font-medium text-[#1a1a1a]">Av ReseNansen</span>
                <span>•</span>
                <span>67 kommentarer</span>
                <span>•</span>
                <span>892 läsningar</span>
              </div>
            </article>

            {/* Article list */}
            {mockTopics.slice(0, 4).map((topic, i) => (
              <article key={topic.id} className={cn("pb-5", i < 3 && "border-b border-gray-200")}>
                <div className="flex gap-4">
                  <span className="text-2xl font-serif font-bold text-gray-300">{i + 2}.</span>
                  <div>
                    <h3 className="text-xl font-serif font-bold text-[#1a1a1a] hover:text-gray-600 cursor-pointer leading-snug">
                      {topic.title}
                    </h3>
                    <p className="text-gray-600 mt-1 font-serif">{topic.excerpt}</p>
                    <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
                      <span>{topic.author}</span>
                      <span>•</span>
                      <span>{topic.replies} svar</span>
                      <span>•</span>
                      <span>{topic.time}</span>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            <div className="bg-white border border-gray-200 p-5">
              <h3 className="font-serif font-bold text-lg text-[#1a1a1a] border-b-2 border-[#1a1a1a] pb-2 mb-4">
                Kategorier
              </h3>
              <div className="space-y-3">
                {mockCategories.map((cat) => (
                  <button key={cat.id} className="w-full flex items-center justify-between text-left hover:bg-gray-50 p-2 -mx-2 transition-colors">
                    <span className="font-serif text-gray-800">{cat.name}</span>
                    <span className="text-sm text-gray-500">{cat.count}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-[#f5f0e8] border border-gray-200 p-5">
              <h3 className="font-serif font-bold text-lg text-[#1a1a1a] border-b-2 border-[#1a1a1a] pb-2 mb-4">
                Mest aktiva
              </h3>
              <div className="space-y-3 text-sm">
                {['Anna_K', 'OrdNörd2000', 'ReseNansen', 'KorsordsFansen'].map((name, i) => (
                  <div key={name} className="flex items-center gap-3">
                    <span className="font-bold text-gray-400">{i + 1}.</span>
                    <span className="font-serif text-gray-800">{name}</span>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

// Design 20: Bibliotek - Lugn akademisk känsla
function DesignLibrary() {
  return (
    <div className="min-h-[700px] bg-gradient-to-b from-[#f8f6f1] to-[#ebe7df]">
      {/* Header */}
      <header className="bg-[#2c3e50] text-white">
        <div className="max-w-5xl mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#c9a959] rounded-sm flex items-center justify-center">
                <FileText className="h-6 w-6 text-[#2c3e50]" />
              </div>
              <div>
                <h1 className="text-2xl font-serif font-bold tracking-wide">Korsordsforum</h1>
                <p className="text-sm text-gray-300">Kunskapsarkivet för ordentusiaster</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Sök i arkivet..."
                  className="w-72 pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-300 outline-none focus:bg-white/20 text-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-8 py-10">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-8">
          <Home className="h-4 w-4" />
          <ChevronRight className="h-4 w-4" />
          <span>Diskussioner</span>
          <ChevronRight className="h-4 w-4" />
          <span className="text-[#2c3e50] font-medium">Alla ämnen</span>
        </div>

        <div className="flex gap-10">
          {/* Main content */}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-serif font-bold text-[#2c3e50]">Diskussioner</h2>
              <Button className="bg-[#c9a959] hover:bg-[#b8994a] text-[#2c3e50] font-medium px-6 py-3 text-base">
                <Plus className="h-5 w-5 mr-2" />
                Nytt ämne
              </Button>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {mockTopics.map((topic, i) => (
                <div
                  key={topic.id}
                  className={cn(
                    "px-6 py-5 hover:bg-[#f8f6f1] transition-colors cursor-pointer",
                    i > 0 && "border-t border-gray-100"
                  )}
                >
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-full bg-[#2c3e50] flex items-center justify-center text-white text-lg font-serif flex-shrink-0">
                      {topic.authorAvatar}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        {topic.pinned && (
                          <Pin className="h-4 w-4 text-[#c9a959]" />
                        )}
                        <h3 className="text-lg font-serif font-semibold text-[#2c3e50] hover:text-[#c9a959] transition-colors">
                          {topic.title}
                        </h3>
                        {topic.solved && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                            Besvarad
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 mt-1 line-clamp-2">{topic.excerpt}</p>
                      <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                        <span className="font-medium text-[#2c3e50]">{topic.author}</span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="h-4 w-4" />
                          {topic.replies} svar
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="h-4 w-4" />
                          {topic.views} läsningar
                        </span>
                        <span>{topic.time}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <aside className="w-64 space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
              <h3 className="font-serif font-bold text-[#2c3e50] mb-4 flex items-center gap-2">
                <Bookmark className="h-5 w-5 text-[#c9a959]" />
                Avdelningar
              </h3>
              <div className="space-y-2">
                {mockCategories.map((cat) => (
                  <button key={cat.id} className="w-full flex items-center gap-3 px-3 py-2 rounded hover:bg-[#f8f6f1] transition-colors text-left">
                    <span className="text-lg">{cat.icon}</span>
                    <span className="text-sm text-gray-700">{cat.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

// Design 21: Klassiskt Brev - Nostalgisk brevstil
function DesignLetter() {
  return (
    <div className="min-h-[700px] bg-[#f4efe4]" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v1H0z' fill='%23e0d8c8' fill-opacity='0.3'/%3E%3C/svg%3E")`,
      backgroundSize: '100% 28px'
    }}>
      {/* Header */}
      <header className="bg-[#8b4513]/10 border-b border-[#8b4513]/20 py-6">
        <div className="max-w-3xl mx-auto px-8 text-center">
          <div className="inline-flex items-center gap-3 text-[#8b4513]">
            <FileText className="h-8 w-8" />
            <h1 className="text-3xl font-serif italic">Korsordssällskapet</h1>
          </div>
          <p className="text-sm text-[#8b4513]/70 mt-2 font-serif">Korrespondens mellan ordvänner</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-8 py-10">
        {/* Letter/post list */}
        <div className="space-y-8">
          {mockTopics.map((topic) => (
            <article 
              key={topic.id} 
              className="bg-[#fffef9] rounded-sm shadow-md border border-[#d4c5a9] p-8 relative"
              style={{
                boxShadow: '2px 2px 8px rgba(139, 69, 19, 0.1)'
              }}
            >
              {/* Stamp effect */}
              <div className="absolute top-4 right-4 w-16 h-20 bg-[#8b4513]/5 border-2 border-dashed border-[#8b4513]/30 rounded flex items-center justify-center">
                <span className="text-2xl">{mockCategories.find(c => c.name === topic.category)?.icon || '📝'}</span>
              </div>

              {/* Date */}
              <div className="text-sm text-[#8b4513]/60 font-serif italic mb-4">
                {topic.time}
              </div>

              {/* Greeting */}
              <p className="text-[#8b4513] font-serif mb-4">Kära korsordsälskare,</p>

              {/* Subject */}
              <h2 className="text-xl font-serif font-bold text-[#2c1810] mb-4 hover:text-[#8b4513] cursor-pointer">
                {topic.title}
              </h2>

              {/* Content */}
              <p className="text-[#4a3728] font-serif leading-relaxed mb-6">
                {topic.excerpt}
              </p>

              {/* Signature */}
              <div className="border-t border-[#d4c5a9] pt-4 mt-6">
                <p className="text-[#8b4513] font-serif italic text-right">
                  Med vänliga hälsningar,<br />
                  <span className="font-bold not-italic">{topic.author}</span>
                </p>
              </div>

              {/* Footer with replies */}
              <div className="flex items-center gap-6 mt-4 pt-4 border-t border-dashed border-[#d4c5a9] text-sm text-[#8b4513]/70">
                <span className="flex items-center gap-2">
                  <Reply className="h-4 w-4" />
                  {topic.replies} svar
                </span>
                <span className="flex items-center gap-2">
                  <Heart className="h-4 w-4" />
                  {topic.likes} gillningar
                </span>
                {topic.solved && (
                  <span className="flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="h-4 w-4" />
                    Besvarad
                  </span>
                )}
              </div>
            </article>
          ))}
        </div>

        {/* Write new letter button */}
        <div className="text-center mt-10">
          <Button className="bg-[#8b4513] hover:bg-[#6b3410] text-white font-serif px-8 py-3">
            <FileText className="h-5 w-5 mr-2" />
            Skriv ett nytt brev
          </Button>
        </div>
      </main>
    </div>
  );
}

// Design 22: Radio/TV - SVT/SR inspirerat
function DesignBroadcast() {
  return (
    <div className="min-h-[700px] bg-[#f5f5f5]">
      {/* Header */}
      <header className="bg-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-[#e02e3d] rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">K</span>
                </div>
                <span className="text-white font-bold text-xl">Forum</span>
              </div>
              <nav className="hidden md:flex items-center gap-1">
                {['Start', 'Populärt', 'Senaste', 'Kategorier'].map((item) => (
                  <button key={item} className="px-4 py-2 text-gray-300 hover:text-white hover:bg-white/10 rounded transition-colors">
                    {item}
                  </button>
                ))}
              </nav>
            </div>
            <div className="flex items-center gap-4">
              <button className="p-2 text-gray-300 hover:text-white hover:bg-white/10 rounded-full">
                <Search className="h-5 w-5" />
              </button>
              <Button className="bg-[#e02e3d] hover:bg-[#c4272f] text-white">
                Logga in
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Category chips */}
        <div className="flex flex-wrap gap-2 mb-8">
          {mockCategories.map((cat) => (
            <button
              key={cat.id}
              className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-gray-200 hover:border-[#e02e3d] hover:text-[#e02e3d] transition-colors text-sm font-medium"
            >
              <span>{cat.icon}</span>
              {cat.name}
            </button>
          ))}
        </div>

        {/* Content grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mockTopics.map((topic) => (
            <article
              key={topic.id}
              className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
            >
              {/* Category header */}
              <div className={cn("h-2", topic.categoryColor)} />
              
              <div className="p-6">
                {/* Meta */}
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                  <span className="font-medium text-[#e02e3d]">{topic.category}</span>
                  <span>•</span>
                  <span>{topic.time}</span>
                </div>

                {/* Title */}
                <h3 className="text-lg font-bold text-gray-900 group-hover:text-[#e02e3d] transition-colors line-clamp-2 mb-3">
                  {topic.title}
                </h3>

                {/* Excerpt */}
                <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                  {topic.excerpt}
                </p>

                {/* Footer */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
                      {topic.authorAvatar}
                    </div>
                    <span className="text-sm text-gray-700">{topic.author}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <MessageSquare className="h-4 w-4" />
                      {topic.replies}
                    </span>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </main>
    </div>
  );
}

// Design 23: Anslagstavla - Fysisk tavla med lappar
function DesignBulletinBoard() {
  const noteColors = [
    'bg-yellow-100 border-yellow-200',
    'bg-blue-100 border-blue-200',
    'bg-pink-100 border-pink-200',
    'bg-green-100 border-green-200',
    'bg-orange-100 border-orange-200',
  ];

  return (
    <div className="min-h-[700px] bg-[#8B4513] p-8" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
    }}>
      {/* Cork board frame */}
      <div className="bg-[#d4a574] rounded-lg p-6 shadow-2xl" style={{
        boxShadow: 'inset 0 0 20px rgba(0,0,0,0.3), 0 10px 30px rgba(0,0,0,0.4)'
      }}>
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#4a3728] drop-shadow-sm">
            📌 Korsordssällskapets Anslagstavla
          </h1>
          <p className="text-[#6b5344] mt-2">Häng upp dina frågor och tips!</p>
        </div>

        {/* Notes grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mockTopics.map((topic, i) => (
            <div
              key={topic.id}
              className={cn(
                "relative p-5 rounded shadow-lg border-2 transform hover:-rotate-1 hover:scale-105 transition-transform cursor-pointer",
                noteColors[i % noteColors.length]
              )}
              style={{
                transform: `rotate(${(i % 3 - 1) * 2}deg)`,
                boxShadow: '3px 3px 10px rgba(0,0,0,0.2)'
              }}
            >
              {/* Pin */}
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-red-500 border-2 border-red-600 shadow-md" />

              {/* Category */}
              <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">
                {topic.category}
              </span>

              {/* Title */}
              <h3 className="text-lg font-bold text-gray-800 mt-2 leading-snug">
                {topic.title}
              </h3>

              {/* Content */}
              <p className="text-sm text-gray-600 mt-2 line-clamp-3">
                {topic.excerpt}
              </p>

              {/* Author */}
              <div className="mt-4 pt-3 border-t border-gray-300/50 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">— {topic.author}</span>
                <span className="text-xs text-gray-500">{topic.replies} svar</span>
              </div>

              {topic.solved && (
                <div className="absolute top-4 right-4 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow">
                  <CheckCircle2 className="h-5 w-5 text-white" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add note button */}
        <div className="text-center mt-8">
          <Button className="bg-yellow-200 hover:bg-yellow-300 text-gray-800 border-2 border-yellow-300 shadow-lg font-medium px-6 py-3">
            <Plus className="h-5 w-5 mr-2" />
            Häng upp en ny lapp
          </Button>
        </div>
      </div>
    </div>
  );
}

// Design 24: Bokhandel - Varm träkänsla
function DesignBookstore() {
  return (
    <div className="min-h-[700px] bg-gradient-to-b from-[#2c1810] to-[#1a0f0a]">
      {/* Header */}
      <header className="bg-[#1a0f0a]/80 backdrop-blur border-b border-[#4a3020]">
        <div className="max-w-5xl mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#c9a959] rounded flex items-center justify-center">
                <FileText className="h-6 w-6 text-[#2c1810]" />
              </div>
              <div>
                <h1 className="text-2xl font-serif text-[#f4e4c8]">Ordböckernas Hörna</h1>
                <p className="text-sm text-[#a89070]">Korsordsforum för bokälskare</p>
              </div>
            </div>
            <Button className="bg-[#c9a959] hover:bg-[#d4b46a] text-[#2c1810] font-medium">
              Skriv inlägg
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-8 py-10">
        {/* Bookshelf style categories */}
        <div className="mb-10">
          <h2 className="text-lg font-serif text-[#c9a959] mb-4">Avdelningar</h2>
          <div className="flex gap-3">
            {mockCategories.map((cat) => (
              <button
                key={cat.id}
                className="flex flex-col items-center gap-2 px-4 py-3 bg-[#3d2a1f] hover:bg-[#4d3a2f] border border-[#5d4a3f] rounded transition-colors group"
              >
                <span className="text-2xl">{cat.icon}</span>
                <span className="text-sm text-[#d4c4a8] group-hover:text-[#f4e4c8]">{cat.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Thread list styled like book spines */}
        <div className="space-y-3">
          {mockTopics.map((topic) => (
            <div
              key={topic.id}
              className="bg-gradient-to-r from-[#3d2a1f] to-[#4d3a2f] border border-[#5d4a3f] rounded-lg p-5 hover:from-[#4d3a2f] hover:to-[#5d4a3f] transition-colors cursor-pointer group"
            >
              <div className="flex items-center gap-5">
                {/* Book spine indicator */}
                <div className={cn("w-2 h-16 rounded-full", topic.categoryColor)} />

                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    {topic.pinned && <Pin className="h-4 w-4 text-[#c9a959]" />}
                    <h3 className="text-lg font-serif text-[#f4e4c8] group-hover:text-[#c9a959] transition-colors">
                      {topic.title}
                    </h3>
                    {topic.solved && (
                      <span className="px-2 py-0.5 bg-green-900/50 text-green-400 text-xs rounded">
                        Löst
                      </span>
                    )}
                  </div>
                  <p className="text-[#a89070] mt-1 text-sm">{topic.excerpt}</p>
                  <div className="flex items-center gap-4 mt-3 text-sm text-[#8a7a60]">
                    <span className="text-[#c9a959]">{topic.author}</span>
                    <span>•</span>
                    <span>{topic.replies} svar</span>
                    <span>•</span>
                    <span>{topic.time}</span>
                  </div>
                </div>

                <ChevronRight className="h-5 w-5 text-[#6a5a40] group-hover:text-[#c9a959] transition-colors" />
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

// Design 25: Kafé - Mysig atmosfär
function DesignCafe() {
  return (
    <div className="min-h-[700px] bg-[#f5ebe0]">
      {/* Header */}
      <header className="bg-[#6b4423] text-white">
        <div className="max-w-5xl mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-3xl">☕</span>
              <div>
                <h1 className="text-2xl font-bold">Korsordskaféet</h1>
                <p className="text-[#d4b896] text-sm">Ta en fika och diskutera ord</p>
              </div>
            </div>
            <nav className="flex items-center gap-6 text-[#d4b896]">
              <button className="hover:text-white transition-colors">Bord</button>
              <button className="hover:text-white transition-colors">Menyn</button>
              <button className="hover:text-white transition-colors">Stamgäster</button>
            </nav>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-8 py-10">
        {/* Welcome message */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-8 border border-[#e8d5c4]">
          <div className="flex items-center gap-4">
            <span className="text-4xl">🧩</span>
            <div>
              <h2 className="text-xl font-bold text-[#6b4423]">Välkommen till Korsordskaféet!</h2>
              <p className="text-[#8b6b4a]">Här kan du slå dig ner och prata korsord med likasinnade.</p>
            </div>
          </div>
        </div>

        {/* Table/conversation list */}
        <div className="grid gap-4">
          {mockTopics.map((topic) => (
            <div
              key={topic.id}
              className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer border border-[#e8d5c4] group"
            >
              <div className="flex gap-5">
                {/* Avatar */}
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#d4a574] to-[#c49464] flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                  {topic.authorAvatar}
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-bold text-[#6b4423]">{topic.author}</span>
                    <span className="text-sm text-[#a89070]">{topic.time}</span>
                    {topic.pinned && (
                      <span className="px-2 py-0.5 bg-[#f5ebe0] text-[#6b4423] text-xs rounded-full font-medium">
                        📌 Fastnad
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-[#3d2a1f] mt-2 group-hover:text-[#6b4423] transition-colors">
                    {topic.title}
                  </h3>
                  <p className="text-[#8b6b4a] mt-1">{topic.excerpt}</p>
                  
                  {/* Engagement */}
                  <div className="flex items-center gap-4 mt-4">
                    <button className="flex items-center gap-1.5 text-sm text-[#a89070] hover:text-[#6b4423] transition-colors">
                      <Heart className="h-4 w-4" />
                      {topic.likes}
                    </button>
                    <button className="flex items-center gap-1.5 text-sm text-[#a89070] hover:text-[#6b4423] transition-colors">
                      <MessageSquare className="h-4 w-4" />
                      {topic.replies} svar
                    </button>
                    {topic.solved && (
                      <span className="flex items-center gap-1.5 text-sm text-green-600">
                        <CheckCircle2 className="h-4 w-4" />
                        Löst
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* New conversation button */}
        <div className="fixed bottom-8 right-8">
          <Button className="bg-[#6b4423] hover:bg-[#5a3a1f] text-white rounded-full px-6 py-3 shadow-lg">
            <Plus className="h-5 w-5 mr-2" />
            Starta ett samtal
          </Button>
        </div>
      </main>
    </div>
  );
}

// Design 26: Trädgård - Naturinspirerad
function DesignGarden() {
  return (
    <div className="min-h-[700px] bg-gradient-to-b from-[#e8f5e9] to-[#c8e6c9]">
      {/* Header */}
      <header className="bg-[#2e7d32] text-white">
        <div className="max-w-5xl mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#81c784] rounded-full flex items-center justify-center">
                <span className="text-2xl">🌿</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold">Ordträdgården</h1>
                <p className="text-[#a5d6a7] text-sm">Där kunskap växer och frodas</p>
              </div>
            </div>
            <Button className="bg-[#81c784] hover:bg-[#66bb6a] text-[#1b5e20]">
              <Plus className="h-5 w-5 mr-2" />
              Plantera en fråga
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-8 py-10">
        {/* Garden beds (categories) */}
        <div className="flex gap-4 mb-10 overflow-x-auto pb-2">
          {mockCategories.map((cat) => (
            <button
              key={cat.id}
              className="flex-shrink-0 flex items-center gap-2 px-5 py-3 bg-white/70 backdrop-blur rounded-full border border-[#81c784] hover:bg-white transition-colors"
            >
              <span>{cat.icon}</span>
              <span className="text-[#2e7d32] font-medium">{cat.name}</span>
              <span className="text-sm text-[#66bb6a]">{cat.count}</span>
            </button>
          ))}
        </div>

        {/* Plant/topic list */}
        <div className="space-y-4">
          {mockTopics.map((topic) => (
            <div
              key={topic.id}
              className="bg-white/80 backdrop-blur rounded-2xl p-6 border border-[#a5d6a7] hover:bg-white transition-colors cursor-pointer group"
            >
              <div className="flex gap-5">
                {/* Plant indicator */}
                <div className="flex flex-col items-center gap-1">
                  <div className="w-12 h-12 rounded-full bg-[#e8f5e9] flex items-center justify-center text-2xl">
                    {topic.solved ? '🌻' : '🌱'}
                  </div>
                  <span className="text-xs text-[#66bb6a]">{topic.solved ? 'Blommat' : 'Växer'}</span>
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    {topic.pinned && (
                      <span className="px-2 py-0.5 bg-[#fff9c4] text-[#f9a825] text-xs rounded-full font-medium">
                        ⭐ Utvald
                      </span>
                    )}
                    <h3 className="text-lg font-semibold text-[#1b5e20] group-hover:text-[#2e7d32] transition-colors">
                      {topic.title}
                    </h3>
                  </div>
                  <p className="text-[#558b2f] mt-2">{topic.excerpt}</p>
                  <div className="flex items-center gap-4 mt-3 text-sm text-[#81c784]">
                    <span className="font-medium text-[#2e7d32]">{topic.author}</span>
                    <span>•</span>
                    <span>{topic.replies} skott</span>
                    <span>•</span>
                    <span>{topic.time}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

// Design 27: Antikvariat - Vintage charm
function DesignAntique() {
  return (
    <div className="min-h-[700px] bg-[#f4ece1]" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23d4c4a8' fill-opacity='0.3' fill-rule='evenodd'%3E%3Cpath d='M0 38.59l2.83-2.83 1.41 1.41L1.41 40H0v-1.41zM0 1.4l2.83 2.83 1.41-1.41L1.41 0H0v1.41zM38.59 40l-2.83-2.83 1.41-1.41L40 38.59V40h-1.41zM40 1.41l-2.83 2.83-1.41-1.41L38.59 0H40v1.41zM20 18.6l2.83-2.83 1.41 1.41L21.41 20l2.83 2.83-1.41 1.41L20 21.41l-2.83 2.83-1.41-1.41L18.59 20l-2.83-2.83 1.41-1.41L20 18.59z'/%3E%3C/g%3E%3C/svg%3E")`
    }}>
      {/* Header with vintage styling */}
      <header className="bg-[#3d2914] border-b-4 border-[#c9a959]">
        <div className="max-w-4xl mx-auto px-8 py-8 text-center">
          <div className="inline-block border-2 border-[#c9a959] p-6">
            <h1 className="text-3xl font-serif text-[#c9a959] tracking-widest">ANTIKVARIATET</h1>
            <div className="flex items-center justify-center gap-4 mt-2">
              <span className="w-12 h-px bg-[#c9a959]" />
              <span className="text-[#d4b896] text-sm uppercase tracking-widest">Korsordsforum</span>
              <span className="w-12 h-px bg-[#c9a959]" />
            </div>
            <p className="text-[#a89070] text-sm mt-3 font-serif italic">Est. MMXXIV</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-8 py-10">
        {/* Vintage notice */}
        <div className="bg-[#fff8dc] border border-[#d4c4a8] p-4 mb-8 text-center">
          <p className="text-[#6b5344] font-serif italic">
            "Ett ord är som en vinflaska — det blir bättre med tiden."
          </p>
        </div>

        {/* Thread list with vintage cards */}
        <div className="space-y-6">
          {mockTopics.map((topic) => (
            <article
              key={topic.id}
              className="bg-[#fffef9] border border-[#d4c4a8] p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer relative"
              style={{
                boxShadow: '2px 2px 0 #d4c4a8'
              }}
            >
              {/* Corner decoration */}
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#c9a959]" />
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#c9a959]" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#c9a959]" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#c9a959]" />

              <div className="flex gap-5">
                {/* Vintage number */}
                <div className="w-12 h-12 rounded-full border-2 border-[#c9a959] flex items-center justify-center font-serif text-xl text-[#6b5344] flex-shrink-0">
                  {topic.id}
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="px-2 py-0.5 bg-[#f5ebe0] text-[#8b6b4a] text-xs font-serif uppercase tracking-wide">
                      {topic.category}
                    </span>
                    {topic.pinned && (
                      <span className="text-[#c9a959]">★</span>
                    )}
                  </div>
                  <h3 className="text-xl font-serif text-[#3d2914] hover:text-[#6b5344] transition-colors">
                    {topic.title}
                  </h3>
                  <p className="text-[#8b6b4a] mt-2 font-serif leading-relaxed">{topic.excerpt}</p>
                  <div className="flex items-center gap-4 mt-4 text-sm text-[#a89070] font-serif">
                    <span>Av {topic.author}</span>
                    <span>•</span>
                    <span>{topic.replies} svar</span>
                    <span>•</span>
                    <span>{topic.time}</span>
                    {topic.solved && (
                      <>
                        <span>•</span>
                        <span className="text-[#6b8e23]">✓ Besvarad</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </main>
    </div>
  );
}

// Design 28: Kryssning - Nautisk elegans
function DesignCruise() {
  return (
    <div className="min-h-[700px] bg-gradient-to-b from-[#0c2340] to-[#1a365d]">
      {/* Header */}
      <header className="bg-[#0c2340] border-b border-[#c9a959]/30">
        <div className="max-w-5xl mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-[#c9a959] flex items-center justify-center">
                <span className="text-2xl">⚓</span>
              </div>
              <div>
                <h1 className="text-2xl font-serif text-white tracking-wide">S/S Korsordaren</h1>
                <p className="text-[#c9a959] text-sm">Premium korsordsforum</p>
              </div>
            </div>
            <nav className="flex items-center gap-6">
              {['Däck', 'Salong', 'Bibliotek', 'Kapten'].map((item) => (
                <button key={item} className="text-[#a0b4c8] hover:text-white transition-colors font-medium">
                  {item}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-8 py-10">
        {/* Welcome banner */}
        <div className="bg-gradient-to-r from-[#c9a959]/20 to-transparent border-l-4 border-[#c9a959] p-6 mb-10">
          <h2 className="text-xl font-serif text-white">Välkommen ombord!</h2>
          <p className="text-[#a0b4c8] mt-1">Njut av stimulerande diskussioner medan vi seglar mot nya ordäventyr.</p>
        </div>

        {/* Categories as deck levels */}
        <div className="flex gap-3 mb-8 overflow-x-auto pb-2">
          {mockCategories.map((cat) => (
            <button
              key={cat.id}
              className="flex-shrink-0 flex items-center gap-2 px-5 py-3 bg-white/5 border border-[#c9a959]/30 rounded-lg hover:bg-white/10 hover:border-[#c9a959] transition-all"
            >
              <span className="text-xl">{cat.icon}</span>
              <span className="text-white font-medium">{cat.name}</span>
            </button>
          ))}
        </div>

        {/* Thread list */}
        <div className="space-y-4">
          {mockTopics.map((topic) => (
            <div
              key={topic.id}
              className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-6 hover:bg-white/10 hover:border-[#c9a959]/50 transition-all cursor-pointer group"
            >
              <div className="flex gap-5">
                {/* Avatar with ring */}
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#c9a959] to-[#a08030] p-0.5 flex-shrink-0">
                  <div className="w-full h-full rounded-full bg-[#0c2340] flex items-center justify-center text-[#c9a959] text-xl font-serif">
                    {topic.authorAvatar}
                  </div>
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-[#c9a959] font-medium">{topic.author}</span>
                    <span className="text-[#a0b4c8]/50">•</span>
                    <span className="text-[#a0b4c8] text-sm">{topic.time}</span>
                    {topic.pinned && (
                      <span className="px-2 py-0.5 bg-[#c9a959]/20 text-[#c9a959] text-xs rounded font-medium">
                        ⭐ Kaptens val
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-serif text-white mt-2 group-hover:text-[#c9a959] transition-colors">
                    {topic.title}
                  </h3>
                  <p className="text-[#a0b4c8] mt-2">{topic.excerpt}</p>
                  <div className="flex items-center gap-4 mt-4 text-sm text-[#a0b4c8]/70">
                    <span className="flex items-center gap-1.5">
                      <MessageSquare className="h-4 w-4" />
                      {topic.replies}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Eye className="h-4 w-4" />
                      {topic.views}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Heart className="h-4 w-4" />
                      {topic.likes}
                    </span>
                    {topic.solved && (
                      <span className="flex items-center gap-1.5 text-[#4ade80]">
                        <CheckCircle2 className="h-4 w-4" />
                        Löst
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Floating action button */}
        <div className="fixed bottom-8 right-8">
          <Button className="bg-[#c9a959] hover:bg-[#d4b46a] text-[#0c2340] rounded-full w-14 h-14 shadow-lg">
            <Plus className="h-6 w-6" />
          </Button>
        </div>
      </main>
    </div>
  );
}
